// src/dataObject/DataObject.ts
import {
  generateRowId,
  isMapField,
  isReadonlyField as isReadonlyField2,
  fromStorage,
  isXoneFieldType,
  STRING_FIELD_TYPES,
  NUMERIC_FIELD_TYPES
} from "@xone-web/types";

// src/dirty/DirtyTracker.ts
var DirtyTracker = class {
  original = /* @__PURE__ */ new Map();
  dirtySet = /* @__PURE__ */ new Set();
  /** Inicializa el snapshot original con los valores cargados desde BD. */
  setOriginal(fields) {
    this.original.clear();
    if (fields instanceof Map) {
      for (const [k, v] of fields) this.original.set(k, v);
    } else {
      for (const [k, v] of Object.entries(fields)) this.original.set(k, v);
    }
    this.dirtySet.clear();
  }
  /** Asignacion: marca como dirty si difiere del valor original. */
  track(field, oldValue, newValue) {
    if (!this.original.has(field)) {
      this.original.set(field, oldValue);
    }
    const originalValue = this.original.get(field);
    if (sameValue(originalValue, newValue)) {
      this.dirtySet.delete(field);
    } else {
      this.dirtySet.add(field);
    }
  }
  /** Marca un campo como dirty sin alterar el snapshot (uso interno). */
  markDirty(field) {
    this.dirtySet.add(field);
  }
  isDirty() {
    return this.dirtySet.size > 0;
  }
  isPropertyDirty(field) {
    return this.dirtySet.has(field);
  }
  getDirtyProperties() {
    return [...this.dirtySet];
  }
  getOldValue(field) {
    return this.original.get(field);
  }
  /** Tras un `save` exitoso, el estado actual pasa a ser el original. */
  commit(currentFields) {
    this.original.clear();
    for (const [k, v] of currentFields) this.original.set(k, v);
    this.dirtySet.clear();
  }
  /** Tras `refresh`, restaura snapshot completo. */
  reset(fields) {
    this.setOriginal(fields);
  }
  /**
   * Limpia el flag dirty de un único campo (utilizado por `refresh(propName)`,
   * paridad XOne 1.3.0). No altera el snapshot original.
   */
  clearProperty(field) {
    this.dirtySet.delete(field);
  }
};
function sameValue(a, b) {
  if (Object.is(a, b)) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  return false;
}

// src/proxy/DataObjectProxy.ts
import { isReadonlyField, wrapScriptDate } from "@xone-web/types";

// src/errors/XoneDataError.ts
var XONE_DATA_ERROR_CODES = {
  /** Acceso a campo en minusculas (`self.codigo` lanza esto). */
  FIELD_CASE_INVALID: "XONE.JS.FIELD_CASE_INVALID",
  /** Intento de escribir un campo MAP_* (read-only). */
  READONLY_FIELD: "XONE.DATA.READONLY_FIELD",
  /** Coleccion no cargada cuando una operacion la requiere. */
  NOT_LOADED: "XONE.DATA.NOT_LOADED",
  /** Cursor browse no abierto / ya cerrado. */
  BROWSE_INVALID_STATE: "XONE.DATA.BROWSE_INVALID_STATE",
  /** Storage adapter no inyectado. */
  NO_STORAGE_ADAPTER: "XONE.DATA.NO_STORAGE_ADAPTER",
  /** Argumento invalido (vacio, tipo incorrecto). */
  INVALID_ARGUMENT: "XONE.DATA.INVALID_ARGUMENT",
  /** Item no encontrado. */
  NOT_FOUND: "XONE.DATA.NOT_FOUND",
  /** Operacion cancelada por `<before-save>`/`<before-delete>`. */
  CANCELED_BY_EVENT: "XONE.DATA.CANCELED_BY_EVENT",
  /** JSON invalido en `loadFromJson`. */
  JSON_INVALID: "XONE.DATA.JSON_INVALID",
  /** Coleccion locked. */
  LOCKED: "XONE.DATA.LOCKED"
};
var XoneDataError = class _XoneDataError extends Error {
  code;
  field;
  collection;
  cause;
  constructor(message, init) {
    super(message);
    this.name = "XoneDataError";
    this.code = init.code;
    this.field = init.field;
    this.collection = init.collection;
    this.cause = init.cause;
    Object.setPrototypeOf(this, _XoneDataError.prototype);
  }
};

// src/proxy/DataObjectProxy.ts
var XONE_API_ALIASES = {
  ownerCollection: { getter: "getOwnerCollection", setter: "setOwnerCollection" },
  OwnerCollection: { getter: "getOwnerCollection", setter: "setOwnerCollection" },
  ownerApp: { getter: "getOwnerApp" },
  OwnerApp: { getter: "getOwnerApp" },
  contentsCount: { getter: "getContentsCount" },
  ContentsCount: { getter: "getContentsCount" },
  dirty: { getter: "getDirty" },
  Dirty: { getter: "getDirty" }
};
var RESERVED_JS_PROPS = /* @__PURE__ */ new Set([
  "then",
  "catch",
  "finally",
  "constructor",
  "prototype",
  "toJSON",
  "toString",
  "valueOf",
  "toLocaleString"
]);
function isPrivateOrReservedProp(prop) {
  if (typeof prop !== "string") return false;
  if (prop.length === 0) return false;
  return prop.startsWith("_") || RESERVED_JS_PROPS.has(prop);
}
function looksLikeFieldAccess(prop, target) {
  if (typeof prop !== "string") return false;
  if (prop.length === 0) return false;
  if (prop in target) return false;
  if (isPrivateOrReservedProp(prop)) return false;
  return /^[A-Za-z][A-Za-z0-9_]*$/.test(prop);
}
function assertUpperCaseField(field) {
  if (!/^[A-Z][A-Z0-9_]*$/.test(field)) {
    throw new XoneDataError(
      `El nombre de campo '${field}' debe estar en MAYUSCULAS con underscores. Los identificadores en minusculas no son campos validos del modelo XOne.`,
      {
        code: XONE_DATA_ERROR_CODES.FIELD_CASE_INVALID,
        field
      }
    );
  }
}
function wrapWithProxy(target) {
  const proxy = new Proxy(target, {
    get(t, prop, receiver) {
      if (prop === Symbol.toPrimitive) {
        return () => "[object DataObject]";
      }
      if (prop === "valueOf" || prop === "toString" || prop === "toLocaleString") {
        const fn = Reflect.get(t, prop, receiver);
        return typeof fn === "function" ? fn : () => "[object DataObject]";
      }
      if (typeof prop === "string") {
        const alias = XONE_API_ALIASES[prop];
        if (alias) {
          const fn = t[alias.getter];
          if (typeof fn === "function") {
            return fn.call(t);
          }
          return fn;
        }
      }
      if (!looksLikeFieldAccess(prop, t)) {
        return Reflect.get(t, prop, receiver);
      }
      const field = prop;
      assertUpperCaseField(field);
      const value = t.getValue(field);
      if (value instanceof Date) {
        return wrapScriptDate(value);
      }
      return value;
    },
    set(t, prop, value, receiver) {
      if (typeof prop === "string") {
        const alias = XONE_API_ALIASES[prop];
        if (alias) {
          if (alias.setter) {
            const fn = t[alias.setter];
            if (typeof fn === "function") {
              fn.call(t, value);
              return true;
            }
          }
          throw new XoneDataError(
            `La propiedad '${prop}' es read-only.`,
            {
              code: XONE_DATA_ERROR_CODES.READONLY_FIELD,
              field: prop
            }
          );
        }
      }
      if (!looksLikeFieldAccess(prop, t)) {
        return Reflect.set(t, prop, value, receiver);
      }
      const field = prop;
      assertUpperCaseField(field);
      if (isReadonlyField(field)) {
        throw new XoneDataError(
          `El campo '${field}' es read-only.`,
          {
            code: XONE_DATA_ERROR_CODES.READONLY_FIELD,
            field
          }
        );
      }
      t.setValue(field, value);
      return true;
    },
    has(t, prop) {
      if (typeof prop !== "string") return Reflect.has(t, prop);
      if (prop in t) return true;
      if (prop in XONE_API_ALIASES) return true;
      return t.hasField(prop);
    },
    ownKeys(t) {
      const own = Reflect.ownKeys(t);
      const fieldNames = t.getPropertyNames();
      return Array.from(/* @__PURE__ */ new Set([...own, ...fieldNames]));
    },
    getOwnPropertyDescriptor(t, prop) {
      if (typeof prop === "string" && t.hasField(prop) && !(prop in t)) {
        return {
          configurable: true,
          enumerable: true,
          writable: !isReadonlyField(prop),
          value: t.getValue(prop)
        };
      }
      return Reflect.getOwnPropertyDescriptor(t, prop);
    }
  });
  target._self = proxy;
  return proxy;
}

// src/pendingWrites.ts
function registry() {
  const g = globalThis;
  if (!g.__xonePendingWrites) g.__xonePendingWrites = /* @__PURE__ */ new Set();
  return g.__xonePendingWrites;
}
function trackPendingWrite(p) {
  const set = registry();
  const wrapped = p;
  set.add(wrapped);
  const remove = () => {
    set.delete(wrapped);
  };
  p.then(remove, remove);
  return p;
}
function pendingWritesCount() {
  return registry().size;
}
async function whenWritesIdle(timeoutMs = 4e3) {
  const set = registry();
  const deadline = Date.now() + timeoutMs;
  while (set.size > 0 && Date.now() < deadline) {
    const remaining = Math.max(0, deadline - Date.now());
    await Promise.race([
      Promise.allSettled([...set]),
      new Promise((resolve) => setTimeout(resolve, remaining))
    ]);
  }
}

// src/crossRowSync.ts
function listeners() {
  const g = globalThis;
  if (!g.__xoneRowWriteListeners) g.__xoneRowWriteListeners = /* @__PURE__ */ new Set();
  return g.__xoneRowWriteListeners;
}
function onRowWrite(listener) {
  const set = listeners();
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}
function broadcastRowWrite(ev) {
  const rowid = (ev.rowid ?? "").trim();
  if (!rowid) return;
  if (!ev.patch || Object.keys(ev.patch).length === 0) return;
  const normalized = { rowid, patch: ev.patch, source: ev.source };
  for (const l of [...listeners()]) {
    try {
      l(normalized);
    } catch {
    }
  }
}

// src/dataObject/DataObject.ts
var DataObject = class {
  fields = /* @__PURE__ */ new Map();
  variables = /* @__PURE__ */ new Map();
  contentsCache = /* @__PURE__ */ new Map();
  _cachedAttributes = /* @__PURE__ */ new Map();
  // Renombrado a `_dirty` (con underscore) para que el `DataObjectProxy`
  // pueda exponer `self.dirty` como alias de `getDirty()` (paridad
  // `XoneDataObject.WrapNormalProperty` Android) sin colisionar con este
  // tracker interno cuando un metodo de la clase se invoca con `this=proxy`.
  _dirty = new DirtyTracker();
  _isNew;
  _owner;
  _parent;
  /** Ver `DataObjectInit.typeSeeded`. */
  _typeSeeded;
  /**
   * Campos que el SCRIPT asignó explícitamente vía `setValue`/`obj.CAMPO = v`
   * (WEAL-49 FIX A). Independiente del dirty-vs-original: un campo se registra
   * aquí AUNQUE el valor asignado coincida con la semilla sintética de tipo
   * (`0`). Es el discriminador correcto entre "el script escribió este campo"
   * y "solo lo sembró `createObject` por tipo" — necesario para que un
   * `obj.BAJA = 0` (mismo valor que la semilla, por tanto NO dirty) viaje al
   * INSERT, sin regresar WEAL-8 (los campos solo sembrados y NUNCA asignados
   * siguen fuera del INSERT → permanecen NULL en BD).
   */
  _assignedFields = /* @__PURE__ */ new Set();
  /** Indice dentro de la coleccion (gestionado por DataCollection). */
  _index = -1;
  /**
   * WEAL-145: marca de "añadido EXPLÍCITAMENTE a un content en memoria" vía
   * `DataCollection.addItem`. Distingue una fila client-only construida a mano
   * por el proyecto (`createObject()` + `addItem()` + `lock()`, p.ej. la
   * calculadora de cuotas de Alivia — `CalculadoraCuota`, poblada desde una
   * respuesta de API sin tabla recargable) del FANTASMA de un `createObject()`
   * de "+Agregar" cancelado (que también es `isNew()` pero NO se añadió). La
   * persistencia de query-state (dataLayer.getQueryState, WEAL-39) excluye los
   * `isNew()` para no resucitar el fantasma; este flag reabre la puerta a las
   * filas que el proyecto añadió a propósito, de modo que sobreviven al
   * 'volver' de una navegación MPA (paridad Android: la activity padre queda
   * viva y conserva su content en memoria).
   */
  _userAdded = false;
  /**
   * WEAL-60: back-ref al `Proxy` scriptable que envuelve a este objeto
   * (`wrapWithProxy`). Se usa como `self` al disparar `<onchange><field>` por
   * escritura de modelo, para que dentro del handler `self.CAMPO` siga pasando
   * por get/setValue. `undefined` para objetos nunca envueltos (tests unitarios).
   */
  _self;
  /**
   * WEAL-247 (ronda 2): silencia el re-disparo del `<onchange>` de campo
   * (WEAL-60, `dispatchFieldOnchangeToRuntime`) cuando este DataObject es un
   * objeto TRANSITORIO de PERSISTENCIA. `createAppContext.save()` (el `self`
   * de página del canal web) construye un DataObject efímero y le vuelca los
   * campos SUCIOS con `setValue` sólo para armar el `write` — pero cada
   * `setValue` re-emitía `${campo}_onchange`, re-ejecutando el handler del
   * proyecto (que en Alivia hace `self.save()` sin `await`: Despacho /
   * Paquete comisión) → SEGUNDA escritura de red por una sola selección. El
   * handler YA corrió cuando el usuario/script asignó el valor en el `self`
   * de página; volver a dispararlo durante la serialización de persistencia
   * es un artefacto del diseño fachada→DataObject, nunca una edición real.
   * La ronda 1 quitó el `fireChange()` redundante del combo, pero esta
   * segunda vía (el propio `save`) quedó viva. Sólo afecta al volcado de
   * persistencia; los `setValue` normales (edición de UI / script) siguen
   * disparando el onchange igual que antes.
   */
  _suppressOnchangeDispatch = false;
  constructor(init) {
    this._owner = init.owner;
    this._parent = init.parent ?? null;
    this._isNew = init.isNew;
    this._typeSeeded = init.typeSeeded ?? /* @__PURE__ */ new Set();
    const def = this._owner.getDefinition();
    for (const field of def.fields) {
      this.fields.set(field.name, field.defaultValue ?? null);
    }
    if (this._isNew && !this.fields.has("ROWID")) {
      this.fields.set("ROWID", generateRowId());
    }
    if (init.initial) {
      for (const [k, v] of Object.entries(init.initial)) {
        this.fields.set(k, v);
      }
      if (this._isNew && !this.fields.get("ROWID")) {
        this.fields.set("ROWID", generateRowId());
      }
    }
    this._dirty.setOriginal(this.fields);
  }
  // -------------------------------------------------------------------------
  // Identificacion y navegacion (SYNC)
  // -------------------------------------------------------------------------
  getOwnerApp() {
    return this._owner.getOwnerApp();
  }
  getOwnerCollection() {
    return this._owner;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#setOwnerCollection`): el parámetro es
   * OBLIGATORIO y debe ser una instancia `DataCollection`. Devuelve `null`
   * en el wrapper Java; aquí devolvemos `void` (TS). Lanza si `coll` no es
   * una DataCollection.
   */
  setOwnerCollection(coll) {
    if (!coll || typeof coll.getName !== "function") {
      throw new XoneDataError(
        "setOwnerCollection: se requiere una instancia DataCollection.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    this._owner = coll;
  }
  getParent() {
    if (this._parent) return this._parent;
    return this._owner.getOwnerObject() ?? null;
  }
  getObjectIndex() {
    return this._index;
  }
  isNew() {
    return this._isNew;
  }
  /**
   * WEAL-145: marca este objeto como añadido a mano a un content en memoria
   * (lo invoca `DataCollection.addItem`). Ver `_userAdded`.
   */
  markUserAdded() {
    this._userAdded = true;
  }
  /** WEAL-145: ¿fue añadido a un content en memoria vía `addItem`? Ver `_userAdded`. */
  wasUserAdded() {
    return this._userAdded;
  }
  toString() {
    const id = this.fields.get("ROWID") ?? this.fields.get("CODIGO") ?? "?";
    return `[DataObject ${this._owner.getName()}#${id}]`;
  }
  // -------------------------------------------------------------------------
  // Valores (SYNC)
  // -------------------------------------------------------------------------
  /** Indica si el campo existe en la definicion o en los datos cargados. */
  hasField(field) {
    if (this.fields.has(field)) return true;
    return this._owner.getDefinition().fields.some((f) => f.name === field);
  }
  /**
   * WEAL-589: `true` si el campo tiene un valor REAL almacenado (recibido del
   * backend o asignado), sin el default por tipo que aplica `getValue`
   * (`""`/`0`). Declarado pero ausente, o `null`, → `false`; `0`/`""`
   * almacenados → `true`. Solo lectura.
   */
  hasStoredValue(fieldName) {
    if (!this.fields.has(fieldName)) return false;
    const v = this.fields.get(fieldName);
    return v !== void 0 && v !== null;
  }
  get(fieldName) {
    return this.getValue(fieldName);
  }
  getValue(fieldName) {
    assertUpperCaseField(fieldName);
    if (!this.fields.has(fieldName) && !this.hasField(fieldName)) {
      return void 0;
    }
    const v = this.fields.get(fieldName);
    if (v !== void 0 && v !== null) return v;
    const def = this._owner.getDefinition().fields.find((f) => f.name === fieldName);
    if (def) {
      const type = def.type;
      if (STRING_FIELD_TYPES.has(type)) return "";
      if (NUMERIC_FIELD_TYPES.has(type)) return 0;
    }
    if (isMapField(fieldName)) return "";
    return v;
  }
  /**
   * Snapshot inmutable de todos los campos (paridad XOne: lectura completa
   * del row para alimentar la resolucion de macros `##FLD_*##`).
   *
   * Devuelve una copia plana (clave: nombre del campo en MAYUSCULAS, valor:
   * el unknown sin tipar). La mutacion del Map devuelto NO afecta al
   * DataObject. Pensado para integraciones que necesitan inspeccionar el row
   * entero — p.ej. el `xone-xml-renderer` al expandir
   * `<prop title="##FLD_*##">` antes de pintar.
   *
   * Coste: O(N) sobre el numero de campos cargados (~10-100 por row tipico).
   */
  getAllFields() {
    const out = {};
    const declared = this._owner.getDefinition().fields;
    for (const [k, v] of this.fields) {
      out[k] = v;
    }
    for (const def of declared) {
      const cur = out[def.name];
      if (cur !== void 0 && cur !== null) continue;
      const type = def.type;
      if (STRING_FIELD_TYPES.has(type)) {
        out[def.name] = "";
      } else if (NUMERIC_FIELD_TYPES.has(type)) {
        out[def.name] = 0;
      }
    }
    return out;
  }
  /**
   * Coerción por TIPO en la ASIGNACIÓN (WEAL-49 FIX B).
   *
   * Paridad Android: `XoneDataObject` coerce el valor asignado al tipo
   * declarado del campo. En web el valor llegaba SIN coercer, así que un
   * `obj.IDTIPO_EXPEDIENTE = self.MAP_ID_TIPO_PRODUCTO` (string `'5'` leído de
   * un binding de UI) quedaba como string `'5'` → `switch(obj.IDTIPO...){case 5}`
   * (igualdad estricta JS) NO casaba (`'5' === 5` es false) → la rama que crea
   * la fila hija (`gen_expediente_vehiculo`) nunca se ejecutaba; y el payload
   * viajaba con `'5'` en vez de `5`.
   *
   * Alcance conservador: solo coercemos tipos **numéricos** (`N`, `N1`-`N6`,
   * `NC`) reutilizando el mapa canónico `fromStorage` de `@xone-web/types`. NO
   * tocamos textuales/fechas para no alterar el formato de wire de campos como
   * `FECHA_CREACION` (una fecha string que hoy viaja tal cual). Campos `MAP_*`,
   * los no declarados en la coll y los ya `number` se dejan intactos. Si el
   * valor no es convertible (cadena no numérica), se preserva el crudo (Android
   * tampoco aborta la asignación).
   */
  _coerceAssignedValue(field, value) {
    if (value === null || value === void 0) return value;
    if (typeof value === "number") return value;
    const def = this._owner.getDefinition().fields.find((f) => f.name === field);
    if (!def) return value;
    const type = def.type;
    if (!isXoneFieldType(type) || !NUMERIC_FIELD_TYPES.has(type)) return value;
    try {
      return fromStorage(value, type);
    } catch {
      return value;
    }
  }
  setValue(field, value) {
    assertUpperCaseField(field);
    if (isReadonlyField2(field)) {
      throw new XoneDataError(
        `El campo '${field}' es read-only.`,
        {
          code: XONE_DATA_ERROR_CODES.READONLY_FIELD,
          field,
          collection: this._owner.getName()
        }
      );
    }
    if (value === void 0) return;
    const coerced = this._coerceAssignedValue(field, value);
    const oldValue = this.fields.get(field);
    this.fields.set(field, coerced);
    this._assignedFields.add(field);
    this._dirty.track(field, oldValue, coerced);
    this._owner._notifyItemFieldSet(this, field);
    const bus = this._owner._getBus();
    if (bus && bus.hasHandlers(`onchange:${field}`)) {
      void bus.emitObject(`onchange:${field}`, {
        self: this,
        locals: { field, oldValue, newValue: coerced }
      });
    }
    if (!this._suppressOnchangeDispatch && fieldValueChanged(oldValue, coerced)) {
      dispatchFieldOnchangeToRuntime(
        this._self ?? this,
        this._owner.getName(),
        field,
        coerced
      );
    }
  }
  /**
   * Variante que espera al bus `<onchange>` para coordinar handlers async.
   * El generador puede insertar `await self.setValueAsync(...)` cuando el
   * `<onchange>` tenga side-effects que deben completarse antes de seguir.
   */
  async setValueAsync(field, value) {
    assertUpperCaseField(field);
    if (isReadonlyField2(field)) {
      throw new XoneDataError(
        `El campo '${field}' es read-only.`,
        {
          code: XONE_DATA_ERROR_CODES.READONLY_FIELD,
          field,
          collection: this._owner.getName()
        }
      );
    }
    if (value === void 0) return;
    const coerced = this._coerceAssignedValue(field, value);
    const oldValue = this.fields.get(field);
    this.fields.set(field, coerced);
    this._assignedFields.add(field);
    this._dirty.track(field, oldValue, coerced);
    this._owner._notifyItemFieldSet(this, field);
    const bus = this._owner._getBus();
    if (bus) {
      await bus.emitObject(`onchange:${field}`, {
        self: this,
        locals: { field, oldValue, newValue: coerced }
      });
    }
  }
  getAllContentNames() {
    return (this._owner.getDefinition().contents ?? []).map((c) => c.name);
  }
  getFieldPropertyValue(field, attribute) {
    const cacheKey = `${field}:${attribute}`;
    const cached = this._cachedAttributes.get(cacheKey);
    if (cached !== void 0) return cached;
    const def = this._owner.getDefinition().fields.find((f) => f.name === field);
    if (!def) return void 0;
    const known = def[attribute];
    if (known !== void 0) return known;
    return def.attributes?.[attribute];
  }
  setFieldPropertyValue(field, attribute, value) {
    const strValue = value == null ? null : String(value);
    const cacheKey = `${field}:${attribute}`;
    if (strValue !== null) {
      if (!strValue.startsWith("##FLD_")) {
        this._cachedAttributes.set(cacheKey, strValue);
      }
    } else {
      this._cachedAttributes.delete(cacheKey);
    }
    this._owner._notifyItemFieldSet(this, field, "property");
    const bus = this._owner._getBus();
    if (bus) {
      void bus.emitObject(`propertychange:${field}`, {
        self: this,
        locals: { field, attribute, value: strValue }
      });
    }
    return value;
  }
  /**
   * WEAL-111: overrides de atributo por-item fijados en runtime vía
   * `setFieldPropertyValue` (`img`, `forecolor`, `title`…).
   *
   * El re-render de un `<contents>` reconstruye las filas desde la plantilla
   * SSR, así que un override de propiedad puesto por script se perdería en el
   * siguiente repintado. El island los re-aplica sobre la fila recién
   * construida leyendo esta lista. Las macros `##FLD_` NO se cachean (se
   * re-evalúan dinámicamente), así que nunca aparecen aquí.
   */
  getCachedFieldAttributes() {
    const out = [];
    for (const [key, value] of this._cachedAttributes) {
      const sep = key.indexOf(":");
      if (sep <= 0) continue;
      out.push({
        field: key.slice(0, sep),
        attribute: key.slice(sep + 1),
        value
      });
    }
    return out;
  }
  clearCachedAttributes() {
    this._cachedAttributes.clear();
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#getNodePropertyValue`): 3 o 4 parámetros.
   * Devuelve el valor del atributo `attribute` del item `itemName` situado dentro
   * del nodo `nodeName` declarado en la colección. Si `evaluate=true`, evalúa la
   * expresión XOne contenida en el atributo. MVP: lookup directo en la definición.
   */
  getNodePropertyValue(_nodeName, _itemName, _attribute, _evaluate) {
    return void 0;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#setNodePropertyValue`): requiere 4 params
   * `(nodeName, itemName, attribute, value)`. Modifica en runtime el atributo XML.
   * MVP: no-op (la mutación efectiva la hace `14-xml-renderer`).
   */
  setNodePropertyValue(nodeName, itemName, attribute, _value) {
    if (!nodeName || !itemName || !attribute) {
      throw new XoneDataError(
        "setNodePropertyValue: nodeName, itemName y attribute son obligatorios.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
  }
  getContentAttr(contentsName, attrName) {
    const def = this._owner.getDefinition().contents?.find(
      (c) => c.name === contentsName
    );
    if (!def) return "";
    if (attrName === "src" || attrName === "coll") return def.src;
    return def.attributes?.[attrName] ?? "";
  }
  // -------------------------------------------------------------------------
  // Variables (SYNC)
  // -------------------------------------------------------------------------
  getVariable(name) {
    return this.variables.get(name);
  }
  getVariables(name) {
    return this.getVariable(name);
  }
  setVariable(name, value) {
    this.variables.set(name, value);
  }
  setVariables(name, value) {
    this.setVariable(name, value);
  }
  getAllVariables() {
    return Object.fromEntries(this.variables);
  }
  // -------------------------------------------------------------------------
  // Dirty (SYNC)
  // -------------------------------------------------------------------------
  getDirty() {
    return this._dirty.isDirty();
  }
  isPropertyDirty(field) {
    return this._dirty.isPropertyDirty(field);
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#getDirtyProperties`): los parámetros se
   * ignoran (la firma Java acepta `Object... params` pero no se consultan). Devuelve
   * el array (vacío si no hay dirty).
   */
  getDirtyProperties(..._ignored) {
    return this._dirty.getDirtyProperties();
  }
  getOldValue(field) {
    return this._dirty.getOldValue(field);
  }
  getOldItem(field) {
    return this.getOldValue(field);
  }
  getContentsCount() {
    return (this._owner.getDefinition().contents ?? []).length;
  }
  // -------------------------------------------------------------------------
  // Persistencia (ASYNC)
  // -------------------------------------------------------------------------
  async save() {
    return trackPendingWrite(this._saveImpl());
  }
  async _saveImpl() {
    const bus = this._owner._getBus();
    if (bus) {
      const r = await bus.emitObject("before-save", { self: this });
      if (r.canceled) return false;
    }
    const def = this._owner.getDefinition();
    const linkedTo = this._owner._getLinkedTo();
    const structuredClient = this._owner._getStructuredClient();
    if (linkedTo && linkedTo.kind === "rest" && structuredClient) {
      await this._saveViaRest(structuredClient, linkedTo.collection, def);
    } else {
      await this._saveViaSql(def);
    }
    this._dirty.commit(this.fields);
    if (bus) await bus.emitObject("after-save", { self: this });
    return true;
  }
  /**
   * Resuelve `[campoClave, valorClave]` para identificar esta fila en el backend
   * REST. Prefiere ROWID cuando está materializado (identidad canónica XOne); si
   * está ausente/vacío, cae a la PK lógica declarada (`idFieldName`, p.ej. `ID`)
   * usando SU nombre de columna — imprescindible para colls WebCore sin ROWID
   * (JOINs), donde filtrar por ROWID no casa ninguna fila. Solo cambia el
   * comportamiento en el caso previamente roto (ROWID vacío): las filas con ROWID
   * real siguen identificándose por ROWID exactamente igual que antes.
   *
   * Interno (usado también por `DataCollection.browseDeleteWithNoRules`); no
   * forma parte de la API pública del modelo XOne.
   */
  _restKey(def) {
    const rowidVal = this.fields.get("ROWID");
    const hasRowid = rowidVal != null && String(rowidVal).trim() !== "";
    if (hasRowid) return ["ROWID", String(rowidVal)];
    const idField = def.idFieldName ?? "ROWID";
    return [idField, String(this.fields.get(idField) ?? "")];
  }
  /**
   * Campos que deben persistirse en un INSERT (objeto nuevo).
   *
   * Paridad Android (`XoneDataObject`): un objeto nuevo solo persiste los
   * campos que el script REALMENTE asignó (+ los `defaultValue` explícitos
   * del `.xne`). Los defaults SINTÉTICOS de tipo que siembra
   * `DataCollection.createObject` (numéricos → 0) existen solo para que el
   * valor sea legible en memoria y NO deben viajar al INSERT: enviarlos
   * escribe ceros en columnas que el dominio espera NULL. Caso real WEAL-8:
   * `cliente.NUMERO` (correlativo de contrato, UNIQUE, asignado más tarde en
   * el ciclo de vida) recibía `0` en cada alta de lead y colisionaba con la
   * fila preexistente que ya tenía `NUMERO = 0` → duplicate entry →
   * `errorprocdata`. En Android queda NULL y un índice UNIQUE admite N NULLs.
   *
   * El discriminador es el conjunto `_assignedFields` (WEAL-49 FIX A): un
   * campo se persiste si el script lo ASIGNÓ explícitamente (`setValue`),
   * aunque el valor coincida con la semilla sintética y por tanto NO sea
   * dirty. Antes el discriminador era solo el tracker dirty, lo que
   * introducía la "Limitación conocida" descrita abajo.
   *
   * WEAL-49 (resuelto por FIX A): un `objNuevo.BAJA = 0` (mismo valor que la
   * semilla `0`, por tanto NO dirty) ANTES se omitía → la fila se insertaba
   * con `BAJA = NULL` y la lista de lectura (que filtra `AND e.BAJA = 0`) la
   * dejaba invisible ("no se guarda"). Ahora `BAJA` está en `_assignedFields`
   * → viaja al INSERT con `0`. WEAL-8 se preserva: `cliente.NUMERO` NUNCA se
   * asigna (solo se siembra por tipo) → no está en `_assignedFields` → sigue
   * fuera del INSERT → permanece NULL (el UNIQUE admite N NULLs).
   */
  _insertFields(def) {
    const dirty = new Set(this._dirty.getDirtyProperties());
    return def.fields.map((f) => f.name).filter((name) => {
      if (isMapField(name)) return false;
      if (name === "ROWID") return true;
      if (!this._typeSeeded.has(name)) return true;
      return dirty.has(name) || this._assignedFields.has(name);
    });
  }
  async _saveViaRest(client, collection, def) {
    if (this._isNew) {
      const row = {};
      for (const name of this._insertFields(def)) {
        row[name] = this.fields.get(name) ?? null;
      }
      const rowid = this.fields.get("ROWID");
      if (rowid) row["ROWID"] = rowid;
      const created = await client.create(collection, row);
      if (!this.fields.get("ROWID")) {
        this.fields.set("ROWID", created.ROWID);
      }
      this._stampGeneratedId(def, created.newId);
      this._isNew = false;
    } else {
      const dirtyFields = this._dirty.getDirtyProperties().filter((n) => !isMapField(n));
      if (dirtyFields.length === 0) return;
      const [keyField, keyValue] = this._restKey(def);
      const patch = {};
      for (const f of dirtyFields) {
        patch[f] = this.fields.get(f) ?? null;
      }
      await client.update(collection, keyValue, patch, keyField);
      this._broadcastRowWrite(patch);
    }
  }
  /**
   * Difunde el patch de una escritura por ROWID al canal de sincronización
   * cruzada (`crossRowSync`). Sólo cuando el ROWID está materializado. Ver
   * `crossRowSync.ts` y `DataCollection._applyRowWrite`.
   */
  _broadcastRowWrite(patch) {
    const rowid = this.fields.get("ROWID");
    const rid = rowid == null ? "" : String(rowid).trim();
    if (!rid) return;
    broadcastRowWrite({ rowid: rid, patch, source: this });
  }
  /**
   * @internal WEAL-199: aplica EN MEMORIA (sin marcar dirty, sin disparar
   * `<onchange>`) un patch difundido por otra instancia que escribió esta misma
   * fila (mismo ROWID). Sólo parchea columnas que esta fila YA porta
   * (`fields.has`), coercionando por tipo, y notifica la reactividad de item por
   * cada campo cambiado para que el `<contents>`/panel ligado repinte. Devuelve
   * `true` si cambió algún valor.
   */
  _applyBackendPatch(patch) {
    let changed = false;
    for (const [field, value] of Object.entries(patch)) {
      if (!this.fields.has(field)) continue;
      const coerced = this._coerceAssignedValue(field, value);
      if (!fieldValueChanged(this.fields.get(field), coerced)) continue;
      this.fields.set(field, coerced);
      this._owner._notifyItemFieldSet(this, field);
      changed = true;
    }
    return changed;
  }
  /**
   * Estampa el ID autogenerado por la BD (WebCore `newId`) en el `idFieldName`
   * de la coll tras un INSERT. GENÉRICO para cualquier proyecto XOne: el campo
   * destino sale de la definición (`def.idFieldName`, misma fuente que WebCore usa
   * server-side para calcular el valor), NUNCA de un nombre hardcodeado por proyecto.
   *
   * Solo estampa cuando:
   *  - el backend aportó un `newId` (WebCore lo omite / devuelve 0 si el idFieldName
   *    no es numérico o no resolvió la fila — p.ej. colls con PK textual o sin
   *    columna de identidad), y
   *  - el objeto era nuevo SIN PK explícita: el `idFieldName` está vacío, null o con
   *    el `0` sintético que siembra `createObject` (si el script fijó una PK propia,
   *    la respetamos y no la pisamos).
   *
   * No marca el campo dirty: es un valor confirmado por la BD, no un cambio pendiente,
   * así un `save()` posterior no lo reenvía en el patch del UPDATE.
   *
   * CAVEAT de divergencia: WebCore devuelve el valor bajo la clave literal `ID` pero
   * calculado sobre SU `getIdFieldName()` (= `idfieldname` declarado en el `.xne`, si
   * no `"ID"`). El generador ahora honra ese `idfieldname` declarado en la definición
   * (`buildCollDefinition`), así que ambos coinciden cuando está declarado; cuando no,
   * ambos caen al default `ID`.
   */
  _stampGeneratedId(def, newId) {
    if (newId == null || newId === 0 || newId === "" || newId === "0") return;
    const idField = def.idFieldName ?? "ID";
    if (idField === "ROWID") return;
    const cur = this.fields.get(idField);
    const curEmpty = cur == null || cur === "" || cur === 0 || cur === "0";
    if (!curEmpty) return;
    this.fields.set(idField, newId);
  }
  async _saveViaSql(def) {
    const storage = this._owner._getStorage();
    const tableName = (def.tablePrefix ?? "") + def.tableName;
    if (this._isNew) {
      const fields = this._insertFields(def);
      const sqlCols = fields.map((n) => n.toLowerCase());
      const placeholders = fields.map(() => "?");
      const values = fields.map((name) => this.fields.get(name) ?? null);
      const sql = `INSERT INTO ${tableName} (${sqlCols.join(", ")}) VALUES (${placeholders.join(", ")})`;
      await storage.executeSql(sql, values);
      this._isNew = false;
    } else {
      const dirtyFields = this._dirty.getDirtyProperties().filter((n) => !isMapField(n));
      if (dirtyFields.length === 0) return;
      const idField = def.idFieldName ?? "ROWID";
      const setSql = dirtyFields.map((f) => `${f.toLowerCase()} = ?`).join(", ");
      const values = dirtyFields.map((f) => this.fields.get(f) ?? null);
      values.push(this.fields.get(idField) ?? null);
      const sql = `UPDATE ${tableName} SET ${setSql} WHERE ${idField.toLowerCase()} = ?`;
      await storage.executeSql(sql, values);
    }
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#deleteObject`): el wrapper Java acepta
   * `Object... params` pero los IGNORA y llama a `DeleteObject()` sin argumentos.
   * Aceptamos varargs por compat con la firma legacy `deleteObject(options)`.
   */
  async deleteObject(..._ignored) {
    const bus = this._owner._getBus();
    if (bus) {
      const r = await bus.emitObject("before-delete", { self: this });
      if (r.canceled) {
        throw new XoneDataError("Borrado cancelado por <before-delete>.", {
          code: XONE_DATA_ERROR_CODES.CANCELED_BY_EVENT,
          collection: this._owner.getName()
        });
      }
    }
    const def = this._owner.getDefinition();
    const idField = def.idFieldName ?? "ROWID";
    const linkedTo = this._owner._getLinkedTo();
    const structuredClient = this._owner._getStructuredClient();
    if (linkedTo && linkedTo.kind === "rest" && structuredClient) {
      const [keyField, keyValue] = this._restKey(def);
      await structuredClient.remove(linkedTo.collection, keyValue, keyField);
    } else {
      const storage = this._owner._getStorage();
      const tableName = (def.tablePrefix ?? "") + def.tableName;
      const sql = `DELETE FROM ${tableName} WHERE ${idField.toLowerCase()} = ?`;
      await storage.executeSql(sql, [this.fields.get(idField) ?? null]);
    }
    this._owner._removeFromItems(this);
    if (bus) await bus.emitObject("after-delete", { self: this });
    return true;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#refresh`): 0 o 1 parámetro.
   * - Sin argumentos: relee TODOS los campos desde BD descartando cambios en memoria.
   * - Con un string `propName`: refresca únicamente ese campo.
   * - Si se pasa un valor no-string, lanza `XoneGenericException` (paridad mobile).
   * - Más de 1 parámetro lanza error.
   */
  async refresh(propName) {
    if (propName !== void 0 && typeof propName !== "string") {
      throw new XoneDataError(
        "refresh: el par\xE1metro debe ser un string (nombre de campo) o omitirse.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    if (this._isNew) return false;
    const def = this._owner.getDefinition();
    const idField = def.idFieldName ?? "ROWID";
    const linkedTo = this._owner._getLinkedTo();
    const structuredClient = this._owner._getStructuredClient();
    let row;
    if (linkedTo && linkedTo.kind === "rest" && structuredClient) {
      const [keyField, keyValue] = this._restKey(def);
      row = await structuredClient.get(
        linkedTo.collection,
        keyValue,
        keyField
      );
    } else {
      const storage = this._owner._getStorage();
      const tableName = (def.tablePrefix ?? "") + def.tableName;
      const sql = `SELECT * FROM ${tableName} WHERE ${idField.toLowerCase()} = ? LIMIT 1`;
      const result = await storage.executeSql(sql, [this.fields.get(idField) ?? null]);
      row = result.rows[0];
    }
    if (!row) return false;
    if (propName !== void 0) {
      if (Object.prototype.hasOwnProperty.call(row, propName)) {
        this.fields.set(propName, row[propName]);
        this._dirty.clearProperty(propName);
      }
      return true;
    }
    this.fields.clear();
    for (const [k, v] of Object.entries(row)) this.fields.set(k, v);
    this._dirty.reset(this.fields);
    return true;
  }
  clearCaches() {
    this.contentsCache.clear();
  }
  // -------------------------------------------------------------------------
  // Contenidos (ASYNC)
  // -------------------------------------------------------------------------
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#contents`): acepta EXACTAMENTE 1 parámetro
   * — el nombre del `<contents>` (string) o su índice posicional (número). No hay
   * `options`. Lanza si se pasan 0 o más de 1.
   */
  async contents(nameOrIndex) {
    return this.getContents(nameOrIndex);
  }
  async getContents(nameOrIndex) {
    if (nameOrIndex === void 0 || nameOrIndex === null) {
      throw new XoneDataError(
        "contents: se requiere el nombre o \xEDndice del <contents>.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    let name;
    if (typeof nameOrIndex === "number") {
      const list = this._owner.getDefinition().contents ?? [];
      const idx = nameOrIndex;
      if (idx < 0 || idx >= list.length) {
        throw new XoneDataError(
          `contents: \xEDndice ${idx} fuera de rango.`,
          {
            code: XONE_DATA_ERROR_CODES.NOT_FOUND,
            collection: this._owner.getName()
          }
        );
      }
      name = list[idx].name;
    } else {
      name = nameOrIndex;
    }
    const cached = this.contentsCache.get(name);
    if (cached) return cached;
    const child = await this._owner._buildContents(name, this);
    this.contentsCache.set(name, child);
    return child;
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#getObjectItem`): acepta 1 o 2 strings
   * `(field, [contentsName])`. Devuelve el `DataObject` referenciado por una
   * propiedad con foreign key. Fuera de ese rango lanza error. MVP: no-op.
   */
  async getObjectItem(field, contentsName) {
    if (!field || typeof field !== "string") {
      throw new XoneDataError(
        "getObjectItem: se requiere el nombre del campo (FK).",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    if (contentsName !== void 0 && typeof contentsName !== "string") {
      throw new XoneDataError(
        "getObjectItem: contentsName debe ser un string.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    return null;
  }
  // -------------------------------------------------------------------------
  // Acciones (ASYNC)
  // -------------------------------------------------------------------------
  async executeNode(nodeName, ..._args) {
    const bus = this._owner._getBus();
    if (!bus) return void 0;
    const r = await bus.emitCustomNode(nodeName, { self: this });
    return r.returnValue;
  }
  // -------------------------------------------------------------------------
  // Metadatos (SYNC)
  // -------------------------------------------------------------------------
  getPropertyNames() {
    return this._owner.getDefinition().fields.map((f) => f.name);
  }
  getPropertyTitle(name) {
    const def = this._owner.getDefinition().fields.find((f) => f.name === name);
    return def?.title ?? name;
  }
  getPropertyGroup(name) {
    const def = this._owner.getDefinition().fields.find((f) => f.name === name);
    return def?.group ?? "";
  }
  // -------------------------------------------------------------------------
  // Clonacion / JSON
  // -------------------------------------------------------------------------
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#clone`): el único parámetro opcional es
   * `copyId: boolean` (no un objeto `options`). Si `copyId=true` conserva ROWID e
   * id del original; por defecto el clon es independiente y obtiene ROWID nuevo.
   */
  async clone(copyId) {
    const data = Object.fromEntries(this.fields);
    if (copyId !== true) {
      data["ROWID"] = generateRowId();
    }
    const cloned = this._owner._instantiate({ ...data }, true);
    return cloned;
  }
  /**
   * @internal WEAL-274: campos de un objeto NUEVO que solo están sembrados
   * por tipo (`typeSeeded`, p.ej. `NUMERO = 0`) y que el script todavía NO
   * ha asignado. Es la información que `_insertFields` usa para dejarlos
   * fuera del INSERT (WEAL-8) y que se PERDÍA al serializar el objeto con
   * `toJson()` para abrir su ficha en otra página MPA (`appData.pushValue`
   * → ObjectState): la ficha reconstruía el objeto con TODOS los campos como
   * "asignados" y el alta enviaba `NUMERO = 0` → duplicate entry sobre el
   * UNIQUE(NUMERO) → `errorprocdata`. El orquestador lo guarda junto al
   * ObjectState para restaurarlo. Vacío si el objeto no es nuevo.
   */
  _unassignedTypeSeeded() {
    if (!this._isNew) return [];
    const dirty = new Set(this._dirty.getDirtyProperties());
    return [...this._typeSeeded].filter(
      (name) => !dirty.has(name) && !this._assignedFields.has(name)
    );
  }
  /** SYNC: serializacion plana en memoria. */
  toJson(_options) {
    return Object.fromEntries(this.fields);
  }
  /** SYNC. */
  toJsonString() {
    return JSON.stringify(this.toJson(), (_k, v) => {
      if (v instanceof Date) return v.toISOString();
      return v;
    });
  }
  /**
   * Aplica un JSON al objeto. ASYNC porque puede insertar en BD si la
   * coleccion espera persistencia inmediata (paridad README §5b.1).
   *
   * Aqui la implementacion estandar es: cargar campos en memoria y devolver
   * el `DataObject`. Si el llamante quiere persistir, llama `save()`.
   */
  async loadFromJson(json) {
    let obj;
    try {
      obj = typeof json === "string" ? JSON.parse(json) : json;
    } catch (err) {
      throw new XoneDataError("JSON invalido en loadFromJson.", {
        code: XONE_DATA_ERROR_CODES.JSON_INVALID,
        cause: err
      });
    }
    if (obj === null || typeof obj !== "object") {
      throw new XoneDataError("loadFromJson espera un objeto.", {
        code: XONE_DATA_ERROR_CODES.JSON_INVALID
      });
    }
    for (const [k, v] of Object.entries(obj)) {
      try {
        assertUpperCaseField(k);
      } catch (err) {
        throw err;
      }
      this.fields.set(k, v);
    }
    this._dirty.setOriginal(this.fields);
    return this;
  }
  // -------------------------------------------------------------------------
  // Binding (stubs - plan 08 §13.3, MVP)
  // -------------------------------------------------------------------------
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#bind`): 3 o 4 parámetros
   * `(controlName, eventName, callback, [context])`. Registra dinámicamente un
   * manejador JavaScript para un evento de un control. Lanza si los nombres
   * están vacíos. MVP: no-op (los handlers reales viven en `04-events`).
   */
  bind(controlName, eventName, _callback, _context) {
    if (!controlName || typeof controlName !== "string") {
      throw new XoneDataError(
        "bind: controlName es obligatorio.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    if (!eventName || typeof eventName !== "string") {
      throw new XoneDataError(
        "bind: eventName es obligatorio.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
  }
  /**
   * Paridad XOne 1.3.0 (`XoneDataObject#unbind`): requiere EXACTAMENTE 2 strings
   * `(controlName, eventName)`. Elimina todos los manejadores asociados.
   */
  unbind(controlName, eventName) {
    if (!controlName || typeof controlName !== "string") {
      throw new XoneDataError(
        "unbind: controlName es obligatorio.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
    if (!eventName || typeof eventName !== "string") {
      throw new XoneDataError(
        "unbind: eventName es obligatorio.",
        {
          code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
          collection: this._owner.getName()
        }
      );
    }
  }
};
function fieldValueChanged(oldValue, newValue) {
  if (oldValue === newValue) return false;
  if (oldValue instanceof Date && newValue instanceof Date) {
    return oldValue.getTime() !== newValue.getTime();
  }
  return true;
}
function dispatchFieldOnchangeToRuntime(self, collName, field, value) {
  const g = globalThis;
  const fn = g.__xoneDispatchFieldOnchange;
  if (typeof fn !== "function") return;
  try {
    fn(self, collName, field, value);
  } catch {
  }
}
function createDataObject(init) {
  const raw = new DataObject(init);
  return wrapWithProxy(raw);
}

// src/dataCollection/DataCollection.ts
import {
  CollectionMacroStore,
  isStructurallyBrokenMacro,
  replaceMacrosSql
} from "@xone-web/macros";
import { generateRowId as generateRowId2 } from "@xone-web/types";

// src/sql/developFieldValueMacros.ts
function developFieldValueMacros(sentence, dataObject, options = {}) {
  if (!sentence) return "";
  if (!dataObject) return sentence;
  const prefix = options.prefix ?? "FLD_";
  const suppress = options.suppressQuotes ?? true;
  const re = new RegExp(
    `('?)##${escapeRegex(prefix)}([A-Z0-9_]+)##('?)`,
    "g"
  );
  return sentence.replace(re, (_match, qL, fieldName, qR) => {
    let value;
    try {
      value = dataObject.getValue(fieldName);
    } catch {
      value = void 0;
    }
    const developed = developObjectValue(value);
    if (suppress) {
      return developed;
    }
    return `${qL}${developed}${qR}`;
  });
}
function developObjectValue(value) {
  if (value === null || value === void 0) return "NULL";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "NULL";
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "1" : "0";
  }
  if (typeof value === "bigint") {
    return value.toString();
  }
  if (value instanceof Date) {
    const pad = (n) => String(n).padStart(2, "0");
    const y = value.getUTCFullYear();
    const m = pad(value.getUTCMonth() + 1);
    const d = pad(value.getUTCDate());
    const hh = pad(value.getUTCHours());
    const mm = pad(value.getUTCMinutes());
    const ss = pad(value.getUTCSeconds());
    return `'${y}-${m}-${d} ${hh}:${mm}:${ss}'`;
  }
  const s = String(value);
  return `'${s.replace(/'/g, "''")}'`;
}
function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// src/pendingLoads.ts
var __pending = /* @__PURE__ */ new Set();
function trackPendingLoad(p) {
  try {
    __pending.add(p);
    void Promise.resolve(p).then(
      () => {
        __pending.delete(p);
      },
      () => {
        __pending.delete(p);
      }
    );
  } catch {
  }
}
function whenPendingLoadsSettled() {
  if (__pending.size === 0) return Promise.resolve();
  return Promise.allSettled([...__pending]).then(() => void 0);
}
function pendingLoadCount() {
  return __pending.size;
}

// src/dsl/DslBuilder.ts
var DslBuilder = class {
  onMissing;
  constructor(opts = {}) {
    this.onMissing = opts.onMissingValue ?? "drop";
  }
  /**
   * Resuelve el input completo. Si `input.filterTemplate` no produce ningún
   * leaf válido tras aplicar la política `onMissingValue=drop`, el resultado
   * no incluye `filter`.
   */
  build(input, context) {
    const out = {};
    if (input.filterTemplate !== void 0) {
      const resolved = this.resolveNode(input.filterTemplate, context);
      if (resolved !== null) out.filter = resolved;
    }
    if (input.order !== void 0) out.order = input.order;
    if (input.page !== void 0) out.page = input.page;
    if (input.project !== void 0) out.project = input.project;
    return out;
  }
  // -- Internos ------------------------------------------------------------
  resolveNode(node, ctx) {
    if (isLeaf(node)) {
      return this.resolveLeaf(node, ctx);
    }
    return this.resolveLogical(node, ctx);
  }
  resolveLeaf(leaf, ctx) {
    if (leaf.op === "IsNull" || leaf.op === "IsNotNull") {
      return { field: leaf.field, op: leaf.op };
    }
    const resolved = this.resolveValue(leaf.value, ctx, leaf.field, leaf.op);
    if (resolved === MISSING) return null;
    return { field: leaf.field, op: leaf.op, value: resolved };
  }
  resolveLogical(node, ctx) {
    if (node.and) {
      const children = this.resolveChildren(node.and, ctx);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0];
      return { and: children };
    }
    if (node.or) {
      const children = this.resolveChildren(node.or, ctx);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0];
      return { or: children };
    }
    if (node.not) {
      const child = this.resolveNode(node.not, ctx);
      return child === null ? null : { not: child };
    }
    return null;
  }
  resolveChildren(nodes, ctx) {
    const out = [];
    for (const child of nodes) {
      const r = this.resolveNode(child, ctx);
      if (r !== null) out.push(r);
    }
    return out;
  }
  resolveValue(expr, ctx, field, op) {
    if (expr === void 0) {
      return this.handleMissing(`${field}/${op}`);
    }
    if (expr === null || typeof expr !== "object") {
      return expr;
    }
    if (Array.isArray(expr)) {
      return expr;
    }
    if ("$ctx" in expr) {
      const v = ctx.getCtxField(expr.$ctx);
      if (v === void 0) return this.handleMissing(`$ctx.${expr.$ctx}`);
      return v;
    }
    if ("$macro" in expr) {
      const v = ctx.getGlobalMacro(expr.$macro);
      if (v === void 0 || v === null) return this.handleMissing(`$macro.${expr.$macro}`);
      return v;
    }
    return expr;
  }
  handleMissing(token) {
    switch (this.onMissing) {
      case "drop":
        return MISSING;
      case "null":
        return null;
      case "throw":
        throw new Error(`[DslBuilder] placeholder no resuelto: ${token}`);
    }
  }
};
var MISSING = /* @__PURE__ */ Symbol("MISSING");
function isLeaf(node) {
  return node.field !== void 0 && node.op !== void 0;
}

// src/dsl/parseWhere.ts
var DEFAULT_LIMITS = Object.freeze({ leaves: 16, depth: 5, inValues: 50 });
var SIMPLE_DIALECT = Object.freeze({ parser: "simple" });
var FULL_DIALECT = Object.freeze({
  parser: "full",
  likeWildcard: "%",
  booleanAs: "number",
  coerceByFieldType: true
});
function resolveFilterDialect(raw) {
  if (raw === "full") return FULL_DIALECT;
  if (raw === null || raw === void 0 || raw === "simple") return SIMPLE_DIALECT;
  if (typeof raw === "object" && raw.parser === "full") {
    const likeWildcard = raw.likeWildcard === "*" ? "*" : "%";
    const booleanAs = raw.booleanAs === "boolean" ? "boolean" : "number";
    const limits = raw.limits ? { ...DEFAULT_LIMITS, ...raw.limits } : void 0;
    return {
      ...FULL_DIALECT,
      likeWildcard,
      booleanAs,
      coerceByFieldType: raw.coerceByFieldType !== false,
      ...limits ? { limits } : {},
      ...raw.pseudoFields ? { pseudoFields: raw.pseudoFields } : {}
    };
  }
  return SIMPLE_DIALECT;
}
var ALWAYS_TRUE = /* @__PURE__ */ Symbol("parseWhere.ALWAYS_TRUE");
var ALWAYS_FALSE = /* @__PURE__ */ Symbol("parseWhere.ALWAYS_FALSE");
function parseWhereToDsl(filter, options) {
  const src = filter.trim();
  if (src === "") return null;
  const dialect = options?.dialect ?? FULL_DIALECT;
  try {
    const p = new Parser(tokenize(src), dialect, options?.fieldTypes);
    const node = p.parseExpr();
    if (!p.atEnd()) return null;
    if (node === ALWAYS_FALSE) return ALWAYS_FALSE;
    if (node !== ALWAYS_TRUE && !withinServerLimits(node, dialect.limits)) return null;
    return node;
  } catch {
    return null;
  }
}
var KEYWORDS = /* @__PURE__ */ new Set(["AND", "OR", "NOT", "LIKE", "IS", "NULL", "IN", "BETWEEN"]);
var NUM_RE = /^-?(0|[1-9]\d*)(\.\d+)?$/;
function numberOrString(raw) {
  if (!NUM_RE.test(raw)) return raw;
  const n = Number(raw);
  if (String(n) !== raw) return raw;
  return Number.isSafeInteger(n) || !Number.isInteger(n) ? n : raw;
}
function withinServerLimits(root, limits) {
  const lim = limits ?? DEFAULT_LIMITS;
  let leaves = 0;
  const walk = (n, depth) => {
    if (n.and || n.or || n.not) {
      const d = depth + 1;
      if (d > lim.depth) return false;
      if (n.and) return n.and.every((c) => walk(c, d));
      if (n.or) return n.or.every((c) => walk(c, d));
      return walk(n.not, d);
    }
    leaves++;
    if (leaves > lim.leaves) return false;
    if ((n.op === "In" || n.op === "Nin") && Array.isArray(n.value) && n.value.length > lim.inValues) return false;
    return true;
  };
  return walk(root, 0);
}
function translateLikePattern(pattern, wildcard) {
  if (wildcard === "%") return pattern;
  return pattern.replace(/%/g, "*").replace(/_/g, "?");
}
function coerceToFieldType(value, fieldType, booleanAs) {
  if (value === null || fieldType === void 0 || fieldType === "") return value;
  const t = fieldType.toUpperCase();
  if (t === "T" || t === "X" || t === "TL" || t === "NC") return typeof value === "number" ? String(value) : value;
  if (/^N\d?$/.test(t) || t === "C") {
    if (typeof value === "number") return value;
    const n = Number(value);
    return typeof value === "string" && value.trim() !== "" && Number.isFinite(n) ? n : value;
  }
  if (t === "B") {
    const truthy = value === 1 || value === true || typeof value === "string" && /^(1|true)$/i.test(value);
    const falsy = value === 0 || value === false || typeof value === "string" && /^(0|false)$/i.test(value);
    if (!truthy && !falsy) return value;
    return booleanAs === "boolean" ? truthy : truthy ? 1 : 0;
  }
  return value;
}
var PSEUDO_FIELD = /* @__PURE__ */ new Set(["SYSDATE", "SYSTIMESTAMP", "CURRENT_DATE", "CURRENT_TIMESTAMP", "ROWNUM"]);
var PSEUDO_VALUE = /* @__PURE__ */ new Set([...PSEUDO_FIELD, "USER", "NOW", "ROWID"]);
function evalLiteral(a, op, b) {
  if (typeof a !== typeof b) throw new Error("mixed literal types");
  const x = a, y = b;
  return op === "=" ? x === y : op === "<>" || op === "!=" ? x !== y : op === "<" ? x < y : op === ">" ? x > y : op === "<=" ? x <= y : x >= y;
}
function tokenize(s) {
  const out = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch === "'") {
      let j = i + 1;
      let v = "";
      for (; ; ) {
        if (j >= s.length) throw new Error("unterminated string");
        if (s[j] === "'") {
          if (s[j + 1] === "'") {
            v += "'";
            j += 2;
            continue;
          }
          j++;
          break;
        }
        v += s[j];
        j++;
      }
      out.push({ t: "str", v });
      i = j;
      continue;
    }
    if (ch === "(" || ch === ")" || ch === ",") {
      out.push({ t: ch, v: ch });
      i++;
      continue;
    }
    const rest = s.slice(i);
    const op = /^(<>|!=|<=|>=|=|<|>)/.exec(rest);
    if (op) {
      out.push({ t: "op", v: op[1] });
      i += op[1].length;
      continue;
    }
    const num = /^-?\d+(\.\d+)?(?![A-Za-z0-9_.-])/.exec(rest);
    if (num) {
      out.push({ t: "num", v: Number(num[0]), raw: num[0] });
      i += num[0].length;
      continue;
    }
    const id = /^[A-Za-z0-9_$][A-Za-z0-9_$.-]*/.exec(rest);
    if (id) {
      out.push({ t: "id", v: id[0] });
      i += id[0].length;
      continue;
    }
    throw new Error(`unexpected char ${ch}`);
  }
  return out;
}
var OP_MAP = {
  "!=": "Ne",
  "<>": "Ne",
  "<=": "Le",
  ">=": "Ge",
  "=": "Eq",
  "<": "Lt",
  ">": "Gt"
};
function andOf(parts) {
  const nodes = [];
  for (const p of parts) {
    if (p === ALWAYS_FALSE) return ALWAYS_FALSE;
    if (p === ALWAYS_TRUE) continue;
    if (p.and) nodes.push(...p.and);
    else nodes.push(p);
  }
  if (nodes.length === 0) return ALWAYS_TRUE;
  return nodes.length === 1 ? nodes[0] : { and: nodes };
}
function orOf(parts) {
  const nodes = [];
  for (const p of parts) {
    if (p === ALWAYS_TRUE) return ALWAYS_TRUE;
    if (p === ALWAYS_FALSE) continue;
    if (p.or) nodes.push(...p.or);
    else nodes.push(p);
  }
  if (nodes.length === 0) return ALWAYS_FALSE;
  return nodes.length === 1 ? nodes[0] : { or: nodes };
}
function notOf(n) {
  if (n === ALWAYS_TRUE) return ALWAYS_FALSE;
  if (n === ALWAYS_FALSE) return ALWAYS_TRUE;
  return n.not ? n.not : { not: n };
}
var Parser = class {
  constructor(toks, dialect = FULL_DIALECT, fieldTypes) {
    this.toks = toks;
    this.dialect = dialect;
    this.fieldTypes = fieldTypes;
    this.pseudoFields = dialect.pseudoFields ? new Set(dialect.pseudoFields.map((f) => f.toUpperCase())) : PSEUDO_FIELD;
  }
  toks;
  dialect;
  fieldTypes;
  i = 0;
  notDepth = 0;
  pseudoFields;
  /** Literal final para `field`: por tipo del campo si se conoce (`coerceByFieldType`), si no por su forma. */
  typed(field, value) {
    if (this.dialect.coerceByFieldType === false || !this.fieldTypes) return value;
    const ft = this.fieldTypes.get(field) ?? this.fieldTypes.get(field.toUpperCase());
    return coerceToFieldType(value, ft, this.dialect.booleanAs ?? "number");
  }
  atEnd() {
    return this.i >= this.toks.length;
  }
  peek() {
    return this.toks[this.i];
  }
  next() {
    const t = this.toks[this.i];
    if (!t) throw new Error("eof");
    this.i++;
    return t;
  }
  isKw(kw) {
    const t = this.peek();
    return !!t && t.t === "id" && t.v.toUpperCase() === kw;
  }
  expectKw(kw) {
    if (!this.isKw(kw)) throw new Error(`expected ${kw}`);
    this.i++;
  }
  expect(t) {
    const k = this.next();
    if (k.t !== t) throw new Error(`expected ${t}`);
    return k;
  }
  parseExpr() {
    const parts = [this.parseAnd()];
    while (this.isKw("OR")) {
      this.i++;
      parts.push(this.parseAnd());
    }
    return orOf(parts);
  }
  parseAnd() {
    const parts = [this.parseUnary()];
    while (this.isKw("AND")) {
      this.i++;
      parts.push(this.parseUnary());
    }
    return andOf(parts);
  }
  parseUnary() {
    if (this.isKw("NOT")) {
      this.i++;
      this.notDepth++;
      try {
        return notOf(this.parseUnary());
      } finally {
        this.notDepth--;
      }
    }
    if (this.peek()?.t === "(") {
      this.i++;
      const e = this.parseExpr();
      this.expect(")");
      return e;
    }
    return this.parseCond();
  }
  parseField() {
    const t = this.next();
    if (t.t !== "id" || KEYWORDS.has(t.v.toUpperCase())) throw new Error("field expected");
    if (this.peek()?.t === "(") throw new Error("function call");
    const name = t.v.replace(/^t1\./i, "");
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name) || this.pseudoFields.has(name.toUpperCase())) throw new Error("field name");
    return name;
  }
  /** Valor escalar. `null` solo para NULL literal (que el llamador rechaza salvo en IS NULL). */
  parseValue() {
    const t = this.next();
    if (t.t === "str") return numberOrString(t.v);
    if (t.t === "num") return numberOrString(t.raw);
    if (t.t === "id") {
      if (t.v.toUpperCase() === "NULL") return null;
      if (KEYWORDS.has(t.v.toUpperCase())) throw new Error("keyword as value");
      if (this.peek()?.t === "(") throw new Error("function as value");
      const up = t.v.toUpperCase();
      if (up === "TRUE") return this.dialect.booleanAs === "boolean" ? true : 1;
      if (up === "FALSE") return this.dialect.booleanAs === "boolean" ? false : 0;
      if (/^t\d+\./i.test(t.v) || PSEUDO_VALUE.has(up)) throw new Error("not a literal");
      return numberOrString(t.v);
    }
    throw new Error("value expected");
  }
  parseCond() {
    const first = this.peek();
    if (first && (first.t === "num" || first.t === "str")) {
      const a = first.t === "num" ? first.v : first.v;
      this.i++;
      const op2 = this.next();
      if (op2.t !== "op") throw new Error("operator expected");
      const bt = this.next();
      if (bt.t !== "num" && bt.t !== "str") throw new Error("literal comparison");
      return evalLiteral(a, op2.v, bt.t === "num" ? bt.v : bt.v) ? ALWAYS_TRUE : ALWAYS_FALSE;
    }
    const field = this.parseField();
    let negate = false;
    if (this.isKw("NOT")) {
      this.i++;
      negate = true;
    }
    if (this.isKw("LIKE")) {
      this.i++;
      const v = this.next();
      if (v.t !== "str") throw new Error("LIKE pattern");
      const node = { field, op: "ILike", value: translateLikePattern(v.v, this.dialect.likeWildcard ?? "%") };
      return negate ? { not: node } : node;
    }
    if (this.isKw("IN")) {
      this.i++;
      this.expect("(");
      const values = [];
      if (this.peek()?.t !== ")") {
        values.push(this.parseValue());
        while (this.peek()?.t === ",") {
          this.i++;
          values.push(this.parseValue());
        }
      }
      this.expect(")");
      if (values.length === 0 || values.some((v) => v === null)) throw new Error("IN list");
      return { field, op: negate ? "Nin" : "In", value: values.map((v) => this.typed(field, v)) };
    }
    if (negate) throw new Error("NOT without LIKE/IN");
    if (this.isKw("IS")) {
      this.i++;
      if (this.isKw("NOT")) {
        this.i++;
        this.expectKw("NULL");
        return { field, op: "IsNotNull" };
      }
      this.expectKw("NULL");
      return { field, op: "IsNull" };
    }
    if (this.isKw("BETWEEN")) {
      this.i++;
      const a = this.parseValue();
      this.expectKw("AND");
      const b = this.parseValue();
      if (a === null || b === null) throw new Error("BETWEEN NULL");
      return { field, op: "Between", value: [this.typed(field, a), this.typed(field, b)] };
    }
    const op = this.next();
    if (op.t !== "op") throw new Error("operator expected");
    const value = this.parseValue();
    if (value === null) {
      if (this.notDepth > 0) throw new Error("NULL under NOT");
      return ALWAYS_FALSE;
    }
    return { field, op: OP_MAP[op.v], value: this.typed(field, value) };
  }
};

// src/dataCollection/DataCollection.ts
function parseSortToOrder(sort) {
  const clauses = [];
  let depth = 0;
  let cur = "";
  for (const ch of sort) {
    if (ch === "(") depth++;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    if (ch === "," && depth === 0) {
      clauses.push(cur);
      cur = "";
    } else cur += ch;
  }
  if (cur.trim() !== "") clauses.push(cur);
  return clauses.map((s) => s.trim()).filter((s) => s !== "").map((clause) => {
    const m = /^(.+?)\s+(asc|desc)$/i.exec(clause);
    if (m) {
      return { field: m[1].trim(), dir: m[2].toLowerCase() === "desc" ? "Desc" : "Asc" };
    }
    return { field: clause, dir: "Asc" };
  });
}
function markLoadFired(item) {
  if (!item || typeof item !== "object") return;
  if (item.__xoneLoadFired === true) return;
  try {
    Object.defineProperty(item, "__xoneLoadFired", {
      value: true,
      enumerable: false,
      writable: false,
      configurable: false
    });
  } catch {
  }
}
var DSL_REJECTIONS_NOT_FROM_FILTER = /* @__PURE__ */ new Set(["XONE.DSL.PAGE_TOO_LARGE", "XONE.DSL.INVALID_CURSOR"]);
function isDslRejection(err) {
  if (!err || typeof err !== "object") return false;
  const e = err;
  return e.status === 400 && typeof e.code === "string" && e.code.startsWith("XONE.DSL.") && !DSL_REJECTIONS_NOT_FROM_FILTER.has(e.code);
}
var DataCollection = class _DataCollection {
  _name;
  _definition;
  _storage;
  _bus;
  _runtime;
  _appData;
  // Owner object del contents (paridad Android `getOwnerObject`). Mutable:
  // una colección `<contents>` compartida puede recibir su owner cuando se
  // resuelve vía `self.getContents(...)` (ver `setOwnerObject`).
  _parent;
  _logger;
  _macros;
  _globalMacros;
  _variables = /* @__PURE__ */ new Map();
  _structuredClient;
  _dslBuilder;
  _definitionResolver;
  _onRowsLoaded;
  _items = [];
  _currentIndex = -1;
  _filter = "";
  _linkFilter = "";
  _sort = "";
  /**
   * Ventana de paginación de la ÚLTIMA carga (`loadAll({size,offset})`), para
   * resolver `next`/`prev`. `null` = la última carga fue completa (sin página).
   * Estado FUNCIONAL de runtime, independiente del atributo VISUAL
   * `rows-per-page` del prop (que el island pasa por-llamada como `{size}`).
   * Ver `loadAll` / `_resolvePage`.
   */
  _lastPage = null;
  /** Nº de filas devueltas por la última carga paginada (heurística `hasMore`). */
  _lastPageRows = 0;
  _fullyLoaded = false;
  _locked = false;
  _cursor = null;
  /**
   * DataObject "owner" del contexto en el que se evalúan las macros
   * `##FLD_X##` y `##OWN_X##`. Réplica del framework Android: cuando un
   * `<contents>` está embebido en una pantalla, ese owner es el DataObject
   * de la pantalla, no las filas de la colección hija. Lo inyecta el
   * bootstrap del SPA antes de `loadAll`/`reload`.
   */
  _fieldMacroOwner = null;
  constructor(opts) {
    this._name = opts.name;
    this._definition = opts.definition;
    this._storage = opts.storage;
    this._bus = opts.bus;
    this._runtime = opts.runtime;
    this._onRowsLoaded = opts.onRowsLoaded;
    this._appData = opts.appData;
    this._parent = opts.parent;
    this._logger = opts.logger ?? console;
    this._globalMacros = opts.macros;
    this._macros = new CollectionMacroStore();
    this._structuredClient = opts.structuredClient;
    this._dslBuilder = new DslBuilder({ onMissingValue: "drop" });
    this._definitionResolver = opts.definitionResolver;
    if (opts.definition.defaultFilter) this._filter = opts.definition.defaultFilter;
    if (opts.definition.defaultSort) this._sort = opts.definition.defaultSort;
    this._unsubRowWrite = onRowWrite((ev) => this._applyRowWrite(ev));
  }
  /** Desuscripción del canal `crossRowSync` (no-op en MPA tras reload). */
  _unsubRowWrite;
  /**
   * @internal WEAL-199: aplica un patch difundido por otra instancia que
   * escribió una fila con este ROWID. Recorre los items cargados y parchea el
   * que coincida (distinto del origen de la escritura).
   */
  _applyRowWrite(ev) {
    if (this._items.length === 0) return;
    for (const it of this._items) {
      if (it === ev.source) continue;
      const rid = String(it.getValue("ROWID") ?? "").trim();
      if (!rid || rid !== ev.rowid) continue;
      it._applyBackendPatch(ev.patch);
    }
  }
  // -------------------------------------------------------------------------
  // Acceso interno (uso de DataObject y factory)
  // -------------------------------------------------------------------------
  /** @internal */
  _getStorage() {
    return this._storage;
  }
  /** @internal */
  _getStructuredClient() {
    return this._structuredClient;
  }
  /** @internal */
  _getLinkedTo() {
    return this._definition.linkedTo;
  }
  /** @internal */
  _getBus() {
    return this._bus;
  }
  /**
   * Callback invocado cuando un item de ESTA colección cambia un valor de
   * campo (vía `setValue` / `obj.CAMPO = v`). Lo usa el orchestrator para
   * re-renderizar el `<contents>` ligado a la colección — reactividad de item
   * (paridad Android `XoneDataObject.postUpdate → refreshProperties`). Coste
   * cero si no se registra (guard en `_notifyItemFieldSet`).
   */
  _onItemFieldSet;
  /** @internal: registra (o limpia con `undefined`) el callback de item-set. */
  setOnItemFieldSet(cb) {
    this._onItemFieldSet = cb;
  }
  /**
   * @internal: notifica un cambio de un item (lo llaman `DataObject.setValue`
   * y, desde WEAL-111, `DataObject.setFieldPropertyValue`).
   *
   * `kind` distingue una mutación de VALOR (`"value"`, default — la que el
   * overlay del shell debe persistir) de un cambio de PROPIEDAD visual
   * (`"property"`: `img`, `forecolor`, `title`…), que solo debe repintar.
   */
  _notifyItemFieldSet(item, field, kind = "value") {
    if (this._onItemFieldSet) this._onItemFieldSet(item, field, kind);
  }
  /** @internal */
  _removeFromItems(obj) {
    const idx = this._items.indexOf(obj);
    if (idx >= 0) this._items.splice(idx, 1);
    this._reindex();
  }
  /**
   * Remoción SOLO-MEMORIA de un item (por referencia o por ROWID), sin
   * disparar el borrado remoto de `deleteObject()` ni eventos de delete.
   *
   * Paridad Android `CancelChanges`/descarte de objeto nuevo: cuando el
   * usuario sale de la vista de edición de un objeto `isNew()` SIN
   * guardarlo, el framework lo descarta de su colección owner — el objeto
   * nunca llegó a la BD, así que la remoción debe ser puramente en memoria
   * (WEAL-39: `deleteItem`/`removeItem` llaman `deleteObject()`, que para
   * un objeto ya persistido intentaría un `drop` remoto).
   *
   * @returns `true` si el item estaba en la colección y se quitó.
   */
  removeFromMemory(target) {
    const obj = typeof target === "string" ? this._items.find(
      (it) => String(it.getValue("ROWID") ?? "") === target
    ) ?? null : target;
    if (!obj) return false;
    const idx = this._items.indexOf(obj);
    if (idx < 0) return false;
    this._items.splice(idx, 1);
    this._reindex();
    return true;
  }
  /** @internal: instancia un DataObject pertenecente a esta coleccion. */
  _instantiate(initial, isNew, typeSeeded) {
    const normalized = {};
    for (const [k, v] of Object.entries(initial)) {
      normalized[k.toUpperCase()] = v;
    }
    const wrapped = createDataObject({
      owner: this,
      initial: normalized,
      isNew,
      typeSeeded
    });
    return wrapped;
  }
  /**
   * @internal: crea/clona una coleccion hija para `<contents>`.
   *
   * Paridad Android (`XoneDataObject.Contents()/LoadContentNode()`,
   * XoneDataObject.java:4391/4437): cada DataObject tiene SU PROPIA
   * instancia de colección por contents (no la colección global), con el
   * `filter` del nodo como link filter y el `sort` como orden por defecto.
   * El lookup por nombre es literal (incluye el prefijo `@` si el XML lo
   * usa), pero toleramos la variante sin/con `@` porque los scripts XOne
   * reales mezclan ambas formas (`getContents("@X")` vs `name="X"`).
   */
  async _buildContents(name, parent) {
    const list = this._definition.contents ?? [];
    const bare = name.startsWith("@") ? name.slice(1) : name;
    const def = list.find((c) => c.name === name) ?? list.find((c) => c.name === bare || c.name === `@${bare}`) ?? list.find((c) => c.src === bare);
    if (!def) {
      throw new XoneDataError(`Contents '${name}' no definido en '${this._name}'.`, {
        code: XONE_DATA_ERROR_CODES.INVALID_ARGUMENT,
        collection: this._name
      });
    }
    const resolved = this._definitionResolver ? await this._definitionResolver(def.src) : null;
    const childDef = resolved ?? {
      name: def.src,
      tableName: def.src.toLowerCase(),
      tablePrefix: this._definition.tablePrefix,
      fields: [],
      contents: []
    };
    const child = new _DataCollection({
      name: def.src,
      definition: childDef,
      storage: this._storage,
      structuredClient: this._structuredClient,
      macros: this._globalMacros,
      ...this._bus ? { bus: this._bus } : {},
      ...this._definitionResolver ? { definitionResolver: this._definitionResolver } : {},
      // El hook recibe el `collName`, así que se propaga tal cual: la coll
      // hija resolverá el `<load>` de SU propia colección (`def.src`).
      ...this._onRowsLoaded ? { onRowsLoaded: this._onRowsLoaded } : {},
      appData: this._appData,
      parent
    });
    const nodeAttrs = def.attributes ?? {};
    const nodeFilter = nodeAttrs["filter"] ?? nodeAttrs["linkfilter"];
    if (nodeFilter) child.setLinkFilter(nodeFilter);
    const nodeSort = nodeAttrs["sort"];
    if (nodeSort && !child.getSort()) child.setSort(nodeSort);
    child.setFieldMacroOwner(parent);
    return child;
  }
  // -------------------------------------------------------------------------
  // Identificacion (SYNC)
  // -------------------------------------------------------------------------
  getName() {
    return this._name;
  }
  name() {
    return this._name;
  }
  getOwnerApp() {
    return this._appData;
  }
  getOwnerObject() {
    return this._parent ?? null;
  }
  ownerObject() {
    return this.getOwnerObject();
  }
  /**
   * Fija el objeto dueño de esta colección (paridad Android: el DataObject que
   * embebe el `<contents>`). En el canal web los `<contents>` se resuelven
   * desde una colección compartida (`self.getContents(name)`), así que el
   * owner se asigna en ese momento — sin él, `row.getParent()` sería null.
   */
  setOwnerObject(obj) {
    this._parent = obj ?? null;
    this._fieldMacroOwner = obj ?? null;
  }
  getIdFieldName() {
    return this._definition.idFieldName ?? "ROWID";
  }
  getCount() {
    return this._items.length;
  }
  count() {
    return this.getCount();
  }
  /**
   * WEAL-142 — cuenta TOTAL de filas en el backend para el filtro/macros
   * ACTUALES (no la cuenta en memoria de `getCount`). La necesita el arranque
   * `start-from-bottom` de un content fluid-load: para abrir por la ÚLTIMA
   * página (los mensajes más recientes) hay que conocer el total y calcular
   * `offset = max(0, total - pageSize)`. Reutiliza el `rawWhere` desarrollado
   * (setFilter/`<coll filter>`) y las macros de colección (`##MACRO##`), que en
   * WebCore Standalone se aplican server-side igual que en `list`. Si la coll no
   * es estructurada (sin `linkedTo`) o no hay cliente, cae a la cuenta en memoria.
   */
  async countRemote() {
    const linkedTo = this._definition.linkedTo;
    if (!this._structuredClient || !linkedTo || linkedTo.kind !== "rest") {
      return this.getCount();
    }
    const supportsRaw = typeof this._structuredClient.supportsRawWhere === "function" && this._structuredClient.supportsRawWhere();
    const macros = this._macros.getAllMacros();
    const filterArg = {};
    if (supportsRaw) {
      const rawWhere = this._developedWhere();
      if (rawWhere) filterArg.rawWhere = rawWhere;
    } else {
      const base = this._dslBuilder.build({
        filterTemplate: this._definition.filterTemplate,
        order: this._definition.defaultDslOrder
      }, this._buildDslContext());
      const merged = this._mergeRuntimeFilter(base);
      if (merged.alwaysFalse) return 0;
      if (merged.unparsedFilter) {
        return this.getCount();
      }
      if (merged.dsl.filter) filterArg.filter = merged.dsl.filter;
    }
    if (Object.keys(macros).length > 0) filterArg.macros = macros;
    try {
      return await this._structuredClient.count(linkedTo.collection, filterArg);
    } catch (err) {
      this._logger.warn(
        `[DataCollection '${this._name}'] countRemote() fall\xF3; se usa la cuenta en memoria`,
        err
      );
      return this.getCount();
    }
  }
  isFull() {
    return this._fullyLoaded;
  }
  isLocked() {
    return this._locked;
  }
  isBrowsing() {
    return this._cursor?.active === true;
  }
  stringKey() {
    const idField = this.getIdFieldName();
    const def = this._definition.fields.find((f) => f.name === idField);
    return def?.type === "T" || def?.type === "X" || idField === "ROWID";
  }
  getMultipleKey() {
    return false;
  }
  getCurrentItem() {
    if (this._cursor?.active) {
      const row = this._cursor.rows[this._cursor.index];
      if (!row) return null;
      return this._instantiate({ ...row }, false);
    }
    if (this._currentIndex >= 0 && this._currentIndex < this._items.length) {
      return this._items[this._currentIndex] ?? null;
    }
    return null;
  }
  getObjectIndex(item) {
    return this._items.indexOf(item);
  }
  /** ROWID 32 hex sin guiones (paridad XOne). */
  generateRowId() {
    return generateRowId2();
  }
  getXmlNode() {
    return this._definition.xmlNode;
  }
  getConnection() {
    return this._storage;
  }
  getDataConnector() {
    return this._storage;
  }
  getAccessString() {
    return this._buildSelectSql({ raw: true });
  }
  getDevelopedAccessString() {
    return this._buildSelectSql({ raw: false });
  }
  getDefinition() {
    return this._definition;
  }
  getAllowedEnterprises() {
    return this._definition.allowedEnterprises ?? "";
  }
  getAllowedUsers() {
    return this._definition.allowedUsers ?? "";
  }
  toString() {
    return `[DataCollection ${this._name} count=${this._items.length}]`;
  }
  // -------------------------------------------------------------------------
  // Filtro / sort (SYNC)
  // -------------------------------------------------------------------------
  getFilter() {
    return this._filter;
  }
  setFilter(value) {
    this._filter = value ?? "";
  }
  getLinkFilter() {
    return this._linkFilter;
  }
  setLinkFilter(value) {
    this._linkFilter = value ?? "";
  }
  getDevelopedFilter() {
    return this._developMacros(this._filter);
  }
  getDevelopedLinkFilter() {
    return this._developMacros(this._linkFilter);
  }
  /**
   * Inyecta el DataObject contra el que se resolverán las macros
   * `##FLD_<CAMPO>##` y `##OWN_<CAMPO>##` en `filter`, `linkFilter`, `sort`
   * y SQL custom. Réplica del comportamiento del framework Android.
   * Pasar `null` lo desconecta.
   */
  setFieldMacroOwner(owner) {
    this._fieldMacroOwner = owner;
  }
  getFieldMacroOwner() {
    return this._fieldMacroOwner;
  }
  getSort() {
    return this._sort;
  }
  setSort(value) {
    if (typeof value === "string") this._sort = value;
    else if (value == null) this._sort = "";
  }
  /**
   * Info de la última carga paginada (o `null` si la última fue completa).
   * Útil para paginación incremental por script
   * (`while (coll.hasMorePages()) await coll.loadAll({ next: true })`).
   */
  getPageInfo() {
    if (!this._lastPage) return null;
    return {
      offset: this._lastPage.offset,
      size: this._lastPage.size,
      rows: this._lastPageRows,
      hasMore: this._lastPageRows >= this._lastPage.size
    };
  }
  /** Heurística: ¿la última página vino llena (probablemente hay más)? */
  hasMorePages() {
    return this._lastPage !== null && this._lastPageRows >= this._lastPage.size;
  }
  /**
   * Resuelve la ventana efectiva `{offset,size}` de una llamada `loadAll` desde
   * las opciones `LoadAllPage` (+ `_lastPage` para `next`/`prev`). Devuelve
   * `null` cuando la llamada NO debe paginar (carga completa): sin opciones, o
   * un objeto sin `size`/`length` ni `next`/`prev` resoluble.
   */
  _resolvePage(p) {
    if (!p) return null;
    const rawSize = typeof p.size === "number" && p.size > 0 ? p.size : p.length;
    const wantSize = typeof rawSize === "number" && rawSize > 0 ? Math.floor(rawSize) : void 0;
    const rawOffset = typeof p.offset === "number" ? p.offset : p.start;
    if (p.next === true || p.prev === true) {
      const base = this._lastPage ?? { offset: 0, size: wantSize ?? 0 };
      const size = wantSize ?? base.size;
      if (size <= 0) return null;
      const offset2 = p.next === true ? base.offset + base.size : Math.max(0, base.offset - size);
      return { offset: offset2, size };
    }
    if (wantSize === void 0) {
      if (typeof rawOffset === "number" && rawOffset > 0) {
        this._logger.warn(
          `[DataCollection '${this._name}'] loadAll({ offset: ${rawOffset} }) sin 'size' \u21D2 carga completa (offset ignorado). Pasa 'size' para una ventana.`
        );
      }
      return null;
    }
    const offset = typeof rawOffset === "number" && rawOffset > 0 ? Math.floor(rawOffset) : 0;
    return { offset, size: wantSize };
  }
  // -------------------------------------------------------------------------
  // Macros (SYNC)
  // -------------------------------------------------------------------------
  getMacro(name) {
    return this._macros.getMacro(name);
  }
  setMacro(name, value) {
    if (isStructurallyBrokenMacro(value)) {
      try {
        globalThis.console?.warn?.(
          `[xone-data] macro estructuralmente rota descartada (WEAL-190) en '${this._name}': setMacro(${JSON.stringify(String(name))}, ${JSON.stringify(value)}) compara un id null/undefined (patr\xF3n '=null'/'=undefined'). Se conserva la macro previa para no vaciar el content vivo. Causa probable: un handler de proyecto lanz\xF3 trabajo as\xEDncrono (p.ej. .save()) sin esperarlo y una continuaci\xF3n hu\xE9rfana construy\xF3 el filtro con el id a\xFAn sin resolver.`,
          { collection: this._name, name: String(name), value }
        );
      } catch {
      }
      return;
    }
    this._macros.setMacro(name, value);
  }
  getAllMacros() {
    return this._macros.getAllMacros();
  }
  getMacroCount() {
    return Object.keys(this._macros.getAllMacros()).length;
  }
  // -------------------------------------------------------------------------
  // Variables (SYNC)
  // -------------------------------------------------------------------------
  getVariable(name) {
    return this._variables.get(name);
  }
  setVariable(name, value) {
    this._variables.set(name, value);
  }
  /**
   * Alias plural de `getVariable` — paridad estricta
   * `XoneDataCollection.getVariables` (`xonedataobject_lib/.../
   * XoneDataCollection.java:5199`). Android expone los dos nombres como
   * `@ScriptAllowed`; muchos `.xne` legacy llaman al plural
   * (`self.ownerCollection.getVariables("##LOGIN_LASTUSER##")` —
   * `xone_projects/AllByXoneNew/LoginColl.xne:64`).
   */
  getVariables(name) {
    return this.getVariable(name);
  }
  /** Alias plural de `setVariable` — paridad Android. */
  setVariables(name, value) {
    this.setVariable(name, value);
  }
  getAllVariables() {
    return Object.fromEntries(this._variables);
  }
  // -------------------------------------------------------------------------
  // Metadatos (SYNC)
  // -------------------------------------------------------------------------
  getPropertyCount() {
    return this._definition.fields.length;
  }
  getPropertyName(index) {
    return this._definition.fields[index]?.name ?? "";
  }
  propertyName(index) {
    return this.getPropertyName(index);
  }
  getPropertyTitle(name) {
    return this._definition.fields.find((f) => f.name === name)?.title ?? name;
  }
  getPropertyGroup(name) {
    return this._definition.fields.find((f) => f.name === name)?.group ?? "";
  }
  getPropType(name) {
    return this._definition.fields.find((f) => f.name === name)?.type ?? "";
  }
  propType(name) {
    return this.getPropType(name);
  }
  getPropVisibility(name) {
    const v = this._definition.fields.find((f) => f.name === name)?.visibility;
    return v === void 0 ? "" : String(v);
  }
  propVisibility(name) {
    return this.getPropVisibility(name);
  }
  getGroupCount() {
    const groups = /* @__PURE__ */ new Set();
    for (const f of this._definition.fields) {
      if (f.group) groups.add(f.group);
    }
    return groups.size;
  }
  getGroup(index) {
    if (index === void 0) return "";
    const groups = Array.from(
      new Set(this._definition.fields.map((f) => f.group).filter(Boolean))
    );
    return groups[index] ?? "";
  }
  group(id) {
    if (typeof id === "number") return this.getGroup(id);
    return id;
  }
  getCollPropertyValue(name) {
    return this._definition[name] ?? "";
  }
  collPropertyValue(name) {
    return this.getCollPropertyValue(name);
  }
  // -------------------------------------------------------------------------
  // Carga / browse (ASYNC)
  // -------------------------------------------------------------------------
  /**
   * Carga la colección. Acepta un objeto de paginación **por-llamada**
   * (`LoadAllPage`) — API funcional de runtime, independiente del atributo
   * visual `rows-per-page` (que el island pasa aquí como `{ size }` al
   * renderizar). Sin `page`, carga completa (comportamiento legacy).
   *
   * - `loadAll()` → completa.
   * - `loadAll({ size: 20 })` → primeros 20.
   * - `loadAll({ size: 20, offset: 40 })` → ventana explícita.
   * - `loadAll({ next: true })` → página siguiente (usa la ventana anterior).
   * - `loadAll({ next: true, append: true })` → scroll infinito (acumula).
   *
   * Nota: el enrutado polimórfico de filtro/sort (`loadAll("F","S", page)`) lo
   * hace el `CollectionWrapper` (dataLayer) antes de delegar aquí; a este nivel
   * el filtro/sort ya están fijados vía `setFilter`/`setSort`.
   */
  async loadAll(page) {
    const done = this._loadAllImpl(page);
    trackPendingLoad(done);
    return done;
  }
  async _loadAllImpl(page) {
    if (this._locked) {
      this._logger.warn(
        `[xone-data] loadAll("${this._name}") ignorado: colecci\xF3n bloqueada (lock()). Si esperabas una recarga, desbloquea antes (unlock()) o usa el refresh del control.`
      );
      return true;
    }
    const eff = this._resolvePage(page);
    const append = page?.append === true && eff !== null && this._items.length > 0;
    const prepend = page?.prepend === true && !append && eff !== null && this._items.length > 0;
    const rows = this._definition.linkedTo ? await this._loadViaLinkedTo(eff) : await this._loadViaStorageAdapter();
    const idField = this.getIdFieldName();
    const prevRuntime = /* @__PURE__ */ new Map();
    if (this._items.length > 0) {
      for (const it of this._items) {
        const key = it.getValue(idField);
        if (key != null) prevRuntime.set(String(key), it.toJson());
      }
    }
    const existingIds = append || prepend ? new Set(this._items.map((it) => String(it.getValue(idField)))) : null;
    const fresh = rows.map((row) => {
      const normalized = {};
      for (const [k, v] of Object.entries(row)) normalized[k.toUpperCase()] = v;
      if (prevRuntime.size > 0) {
        const key = normalized[idField];
        const prev = key != null ? prevRuntime.get(String(key)) : void 0;
        if (prev) {
          for (const [f, v] of Object.entries(prev)) {
            if (!(f in normalized)) normalized[f] = v;
          }
        }
      }
      return normalized;
    }).filter((n) => !existingIds || !existingIds.has(String(n[idField]))).map((n) => this._instantiate(n, false));
    this._items = prepend ? [...fresh, ...this._items] : append ? [...this._items, ...fresh] : fresh;
    this._reindex();
    this._fullyLoaded = true;
    this._currentIndex = this._items.length > 0 ? 0 : -1;
    this._lastPage = eff;
    this._lastPageRows = eff ? rows.length : 0;
    const toEmit = append || prepend ? fresh : this._items;
    if (this._onRowsLoaded && toEmit.length > 0) {
      let ran = false;
      try {
        ran = await this._onRowsLoaded(this._name, toEmit) === true;
      } catch (err) {
        this._logger.error(
          `[load] hook de <load> en '${this._name}' lanzo:`,
          err
        );
      }
      if (ran) {
        for (const item of toEmit) markLoadFired(item);
      }
    }
    if (this._bus) {
      for (const item of toEmit) {
        await this._bus.emitObject("load", { self: item });
      }
    }
    return true;
  }
  /** Plan 14 — rama Storage SQL legacy (pre-NoSqlInClient). */
  async _loadViaStorageAdapter() {
    const sql = this._buildSelectSql({ raw: false });
    const result = await this._storage.executeSql(sql);
    return result.rows;
  }
  /** Plan 14 — rama estructurada (REST + DSL o catálogo). */
  async _loadViaLinkedTo(page) {
    if (!this._structuredClient) {
      throw new XoneDataError(
        `Coleccion '${this._name}' declara linkedTo pero no se inyect\xF3 structuredClient. Aseg\xFArate de pasar XoneStructuredDataClient en createDataCollection.`,
        { code: XONE_DATA_ERROR_CODES.LOCKED, collection: this._name }
      );
    }
    const linkedTo = this._definition.linkedTo;
    const ctx = this._buildDslContext();
    if (linkedTo.kind === "rest") {
      const dsl = this._dslBuilder.build({
        filterTemplate: this._definition.filterTemplate,
        order: this._definition.defaultDslOrder
      }, ctx);
      const supportsRaw = typeof this._structuredClient.supportsRawWhere === "function" && this._structuredClient.supportsRawWhere();
      let mergedDsl;
      let unparsedFilter;
      let mergedWhere = "";
      if (supportsRaw) {
        const rawWhere = this._developedWhere();
        mergedDsl = rawWhere ? { ...dsl, rawWhere } : dsl;
        unparsedFilter = null;
      } else {
        const merged = this._mergeRuntimeFilter(dsl);
        if (merged.alwaysFalse) return [];
        mergedDsl = merged.dsl;
        unparsedFilter = merged.unparsedFilter;
        mergedWhere = merged.mergedWhere ?? "";
      }
      const runtimeSort = typeof this._sort === "string" ? this._sort.trim() : "";
      const dslOrdered = runtimeSort !== "" ? { ...mergedDsl, order: parseSortToOrder(runtimeSort) } : mergedDsl;
      const collMacros = this._macros.getAllMacros();
      const dslWithMacros = Object.keys(collMacros).length > 0 ? { ...dslOrdered, macros: collMacros } : dslOrdered;
      const dslToSend = page ? { ...dslWithMacros, page: { ...dslWithMacros.page ?? {}, size: page.size, offset: page.offset } } : dslWithMacros;
      let rows;
      try {
        rows = await this._structuredClient.list(linkedTo.collection, dslToSend);
      } catch (err) {
        if (!mergedWhere || !isDslRejection(err)) throw err;
        rows = await this._structuredClient.list(linkedTo.collection, { ...dslToSend, filter: dsl.filter });
        this._logger.warn(
          `[DataCollection '${this._name}'] el servidor rechaz\xF3 el filtro runtime (${String(err.code)}); se ha repetido sin \xE9l y se filtra en cliente: ` + mergedWhere
        );
        unparsedFilter = mergedWhere;
      }
      if (unparsedFilter) {
        rows = this._applyClientFilter(rows, unparsedFilter);
      }
      return rows;
    }
    if (linkedTo.kind === "cat") {
      const params = this._resolveNamedParams(this._definition.namedParams, ctx);
      return this._structuredClient.runNamed(linkedTo.name, params);
    }
    throw new XoneDataError(
      `Coleccion '${this._name}' usa linkedTo.external='${linkedTo.provider}' \u2014 fuera del alcance del m\xF3dulo Collections (canal externo).`,
      { code: XONE_DATA_ERROR_CODES.LOCKED, collection: this._name }
    );
  }
  /**
   * Merge runtime filter (from setFilter / loadAll(filter)) into a DSL
   * request. Parses simple SQL-like expressions into DSL FilterNodes and
   * sends them server-side. Returns `{ dsl, unparsed }` — if the filter
   * couldn't be parsed into DSL, `unparsed` carries the raw text so the
   * caller can apply client-side filtering after fetching.
   */
  /**
   * `where` runtime desarrollado = filtro de `setFilter`/`<coll filter>`
   * (`_filter`) AND filtro del nodo `<contents filter>` (`_linkFilter`), con las
   * macros (`##FLD_X##`/`##OWN_X##`/…) resueltas. Es el SQL literal que va como
   * `rawWhere` a WebCore. Paridad Android (combina filtro runtime + link filter).
   */
  _developedWhere() {
    const parts = [this._filter, this._linkFilter].map((f) => this._developMacros(f)).map((s) => typeof s === "string" ? s.trim() : "").filter((s) => s !== "").map((s) => _DataCollection._dropEmptyComparisons(s)).filter((s) => s !== "");
    const uniqueParts = _DataCollection._dedupEquivalentParts(parts);
    if (uniqueParts.length <= 1) return uniqueParts[0] ?? "";
    return uniqueParts.map((p) => `(${p})`).join(" AND ");
  }
  /**
   * WEAL-177 — Deduplica fragmentos SQL de nivel superior que son EQUIVALENTES
   * módulo entrecomillado de literales. Clave de comparación = fragmento sin
   * comillas simples, con espacios colapsados y en minúsculas. Si dos partes
   * comparten clave, se conserva UNA sola; se prefiere la que contiene comillas
   * (representación válida para literales string/GUID) sobre la que no las tiene.
   * Preserva el orden de primera aparición y NO altera fragmentos únicos.
   */
  static _dedupEquivalentParts(parts) {
    const byKey = /* @__PURE__ */ new Map();
    const result = [];
    for (const part of parts) {
      const key = part.replace(/'/g, "").replace(/\s+/g, " ").trim().toLowerCase();
      const existingIdx = byKey.get(key);
      if (existingIdx === void 0) {
        byKey.set(key, result.length);
        result.push(part);
        continue;
      }
      if (!result[existingIdx].includes("'") && part.includes("'")) {
        result[existingIdx] = part;
      }
    }
    return result;
  }
  /**
   * WEAL-140 — Elimina de un fragmento SQL las cláusulas de comparación de
   * nivel superior cuyo lado derecho quedó VACÍO (`CAMPO=`), preservando el
   * resto intacto (mismo texto/formato). Solo actúa sobre comparaciones
   * simples: OR, paréntesis, funciones, `IN`, `LIKE`, `IS NULL` se conservan
   * siempre (no son "comparaciones vacías"). Devuelve '' si no queda nada.
   */
  static _dropEmptyComparisons(expr) {
    const clauses = _DataCollection._splitTopLevelAnd(expr);
    if (clauses.length === 0) return "";
    const kept = clauses.filter((c) => !_DataCollection._isEmptyComparison(c));
    return kept.join(" AND ");
  }
  /** `CAMPO <op>` sin valor a la derecha (RHS vacío). No matchea OR/(). */
  static _isEmptyComparison(clause) {
    const c = clause.trim();
    if (c === "") return true;
    if (/\bOR\b/i.test(c) || c.includes("(") || c.includes(")")) return false;
    return /^[A-Za-z_][\w.]*\s*(!=|<>|<=|>=|=|<|>)\s*$/.test(c);
  }
  /**
   * Parte una expresión SQL por los `AND` de NIVEL SUPERIOR (profundidad de
   * paréntesis 0), devolviendo cada cláusula recortada. Un `AND` dentro de
   * `(...)` (p.ej. `(A OR B) AND C` con subexpresión, o `ifnull(x,0)`) NO
   * corta. Coherente con `_parseLegacyFilterToDsl`/`_applyClientFilter`, que
   * también asumen ` AND ` con espacios como separador.
   */
  static _splitTopLevelAnd(expr) {
    const parts = [];
    let depth = 0;
    let buf = "";
    let i = 0;
    while (i < expr.length) {
      const ch = expr[i];
      if (ch === "(") {
        depth++;
        buf += ch;
        i++;
        continue;
      }
      if (ch === ")") {
        if (depth > 0) depth--;
        buf += ch;
        i++;
        continue;
      }
      if (depth === 0) {
        const m = /^\s+AND\s+/i.exec(expr.slice(i));
        if (m) {
          parts.push(buf.trim());
          buf = "";
          i += m[0].length;
          continue;
        }
      }
      buf += ch;
      i++;
    }
    const tail = buf.trim();
    if (tail !== "") parts.push(tail);
    return parts.filter((p) => p !== "");
  }
  _mergeRuntimeFilter(dsl) {
    const developed = this._developedWhere();
    if (!developed) return { dsl, unparsedFilter: null };
    const dialect = this._filterDialect();
    const parsed = dialect.parser === "full" ? this._parseFullFilterToDsl(developed, dialect) : this._parseLegacyFilterToDsl(developed);
    if (!parsed) {
      this._logger.warn(
        `[DataCollection '${this._name}'] runtime filter could not be parsed to DSL, will fetch all rows and apply client-side: ` + developed
      );
      return { dsl, unparsedFilter: developed };
    }
    if (parsed === ALWAYS_TRUE) return { dsl, unparsedFilter: null };
    if (parsed === ALWAYS_FALSE) return { dsl, unparsedFilter: null, alwaysFalse: true };
    let filter;
    if (!dsl.filter) filter = parsed;
    else if (dialect.parser !== "full") {
      filter = { and: [dsl.filter, parsed] };
    } else {
      const parts = [];
      for (const n of [dsl.filter, parsed]) {
        if (n.and) parts.push(...n.and);
        else parts.push(n);
      }
      filter = { and: parts };
    }
    if (dialect.parser === "full" && !withinServerLimits(filter, dialect.limits)) {
      this._logger.warn(`[DataCollection '${this._name}'] combined filter exceeds server limits, applying client-side: ` + developed);
      return { dsl, unparsedFilter: developed };
    }
    return { dsl: { ...dsl, filter }, unparsedFilter: null, mergedWhere: developed };
  }
  /** Dialecto de filtros del backend (capacidad opt-in); `simple` si no hay cliente o no lo declara. */
  _filterDialect() {
    const c = this._structuredClient;
    return c && typeof c.filterDialect === "function" ? c.filterDialect() : SIMPLE_DIALECT;
  }
  /** Tipos XOne de los campos de la coll (para `coerceByFieldType` del dialecto `full`). */
  _fieldTypeMap() {
    const m = /* @__PURE__ */ new Map();
    for (const f of this._definition.fields) if (f.name && f.type) m.set(f.name, f.type);
    return m;
  }
  /** `(A=1)` → `A=1` (solo un par externo que envuelva TODA la condición y sin paréntesis internos). */
  static _unwrapParens(cond) {
    const m = /^\((.*)\)$/s.exec(cond);
    if (!m) return cond;
    const inner = m[1].trim();
    return /[()]/.test(_DataCollection._stripLiterals(inner)) ? cond : inner;
  }
  /** Sustituye los literales `'…'` por `''` para que OR/paréntesis dentro de ellos no cuenten. */
  static _stripLiterals(s) {
    return s.replace(/'(?:[^']|'')*'/g, "''");
  }
  static _hasOrOrParens(cond) {
    const bare = _DataCollection._stripLiterals(cond);
    return /\bOR\b/i.test(bare) || /[()]/.test(bare);
  }
  static LEGACY_LIKE_RE = /^(.+?)\s+LIKE\s+'(.+)'$/i;
  static LEGACY_IS_NULL_RE = /^(.+?)\s+IS\s+NULL$/i;
  static LEGACY_IS_NOT_NULL_RE = /^(.+?)\s+IS\s+NOT\s+NULL$/i;
  static LEGACY_COMP_RE = /^(.+?)\s*(!=|<>|<=|>=|=|<|>)\s*(.+)$/;
  /**
   * Parser `simple` (dialecto por defecto): el código de xone-data ≤ 1.18 tal cual — solo
   * condiciones `=,!=,<>,<,>,<=,>=`, `LIKE`, `IS [NOT] NULL` encadenadas con AND; cualquier
   * otra cosa → `null` (fallback cliente). Se conserva íntegro para que los proyectos que no
   * declaran dialecto no cambien ni un byte de lo que mandan al servidor.
   */
  _parseLegacyFilterToDsl(filter) {
    const conditions = filter.split(/\s+AND\s+/i).map((c) => _DataCollection._unwrapParens(c.trim())).filter(Boolean);
    if (conditions.length === 0) return null;
    if (conditions.some((c) => _DataCollection._hasOrOrParens(c))) return null;
    const OP_MAP2 = {
      "!=": "Ne",
      "<>": "Ne",
      "<=": "Le",
      ">=": "Ge",
      "=": "Eq",
      "<": "Lt",
      ">": "Gt"
    };
    const nodes = [];
    for (const cond of conditions) {
      const likeM = _DataCollection.LEGACY_LIKE_RE.exec(cond);
      if (likeM) {
        nodes.push({ field: likeM[1].trim().replace(/^t\d+\./i, ""), op: "ILike", value: likeM[2] });
        continue;
      }
      const innM = _DataCollection.LEGACY_IS_NOT_NULL_RE.exec(cond);
      if (innM) {
        nodes.push({ field: innM[1].trim().replace(/^t\d+\./i, ""), op: "IsNotNull" });
        continue;
      }
      const inM = _DataCollection.LEGACY_IS_NULL_RE.exec(cond);
      if (inM) {
        nodes.push({ field: inM[1].trim().replace(/^t\d+\./i, ""), op: "IsNull" });
        continue;
      }
      const m = _DataCollection.LEGACY_COMP_RE.exec(cond);
      if (!m) return null;
      const rawField = m[1].trim().replace(/^t\d+\./i, "");
      const op = OP_MAP2[m[2]];
      if (!op) return null;
      let val = m[3].trim().replace(/^'|'$/g, "");
      if (val !== "" && !isNaN(Number(val))) val = Number(val);
      nodes.push({ field: rawField, op, value: val });
    }
    return nodes.length === 1 ? nodes[0] : { and: nodes };
  }
  /**
   * Filtro runtime (`setFilter`/`<coll filter>`/`<contents filter>`) → `FilterNode`.
   * Delegado en `parseWhereToDsl` (`dsl/parseWhere.ts`): AND/OR/NOT con paréntesis,
   * =, !=, <>, <, >, <=, >=, [NOT] LIKE, IS [NOT] NULL, [NOT] IN (…), BETWEEN. Lo
   * que no es DSL (funciones, campo=campo, macros sin resolver) devuelve `null`
   * y se cae al fallback client-side de antes.
   */
  _parseFullFilterToDsl(filter, dialect) {
    return parseWhereToDsl(filter, { dialect, fieldTypes: this._fieldTypeMap() });
  }
  _applyClientFilter(rows, filter) {
    const conditions = _DataCollection._splitTopLevelAnd(filter).map((c) => _DataCollection._unwrapParens(c));
    const CMP_RE = /^([A-Za-z_][A-Za-z0-9_.]*)\s*(<>|!=|=)\s*('(?:[^']|'')*'|[^\s()]+)$/;
    const evaluable = conditions.filter((cond) => {
      const m = CMP_RE.exec(cond);
      const ok = !!m && !/^t(?!1\.)\d+\./i.test(m[1]) && !/^\s*\(/.test(cond);
      if (!ok) this._logger.warn(`[DataCollection '${this._name}'] client-side filter: condition not evaluable, ignored: ` + cond);
      return ok;
    });
    return rows.filter((row) => {
      for (const cond of evaluable) {
        const m = CMP_RE.exec(cond);
        const field = m[1].trim().replace(/^t1\./i, "");
        const rawVal = m[3].trim();
        if (/^NULL$/i.test(rawVal)) return false;
        const value = rawVal.replace(/^'|'$/g, "").replace(/''/g, "'");
        const rowVal = row[field] ?? row[field.toLowerCase()] ?? row[field.toUpperCase()];
        const eq = String(rowVal) === value;
        if (m[2] === "=" ? !eq : eq) return false;
      }
      return true;
    });
  }
  /**
   * Construye el contexto para `DslBuilder` resolviendo `$ctx` desde el
   * owner DataObject (o el primer fila del filterContext) y `$macro` desde
   * el `globalMacros`.
   */
  _buildDslContext() {
    const owner = this._fieldMacroOwner;
    const macros = this._globalMacros;
    return {
      getCtxField(name) {
        if (!owner) return void 0;
        const upper = name.toUpperCase();
        const v = owner.getValue(upper);
        if (v !== void 0) return v;
        return owner.getValue(name);
      },
      getGlobalMacro(name) {
        if (!macros) return void 0;
        const wrapped = name.startsWith("##") ? name : `##${name}##`;
        const v = macros.getGlobalMacro(wrapped);
        if (v === void 0 || v === null) return void 0;
        return v;
      }
    };
  }
  /**
   * Resuelve parámetros nombrados (Plan 14 §6.1.2): valores literales pasan
   * tal cual; objetos `{ $ctx }` y `{ $macro }` se resuelven con el contexto.
   */
  _resolveNamedParams(params, ctx) {
    if (!params) return {};
    const out = {};
    for (const [key, value] of Object.entries(params)) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const v = value;
        if (typeof v.$ctx === "string") {
          out[key] = ctx.getCtxField(v.$ctx) ?? null;
          continue;
        }
        if (typeof v.$macro === "string") {
          out[key] = ctx.getGlobalMacro(v.$macro) ?? null;
          continue;
        }
      }
      out[key] = value;
    }
    return out;
  }
  async reload(_node) {
    this._items = [];
    this._fullyLoaded = false;
    this._currentIndex = -1;
    return this.loadAll();
  }
  async rebuildLayout(_options) {
  }
  async startBrowse(_options) {
    let rows;
    if (this._definition.linkedTo) {
      rows = await this._loadViaLinkedTo();
    } else {
      const sql = this._buildSelectSql({ raw: false });
      const result = await this._storage.executeSql(sql);
      rows = result.rows;
    }
    this._cursor = {
      rows,
      index: 0,
      active: true
    };
    return true;
  }
  /**
   * #313 — EOF significa "no hay item actual valido", que es lo que significa
   * en Android. Cubre TRES casos, y los tres importan:
   *   - `index < 0`  : el cursor se invalido al desbordar (ver `moveNext`).
   *   - `index >= length` : coleccion VACIA (startBrowse deja index=0 con 0
   *     filas, y 0 >= 0). Sin esta condicion un `while(!isEOF())` sobre una
   *     coleccion vacia entraria una vez a procesar un item inexistente.
   *   - sin cursor o inactivo.
   * El patron clasico `startBrowse -> while(!isEOF()) -> moveNext` se comporta
   * EXACTAMENTE igual que antes: recorre todas las filas y sale al desbordar.
   */
  isEOF() {
    if (!this._cursor || !this._cursor.active) return true;
    const { index, rows } = this._cursor;
    return index < 0 || index >= rows.length;
  }
  async endBrowse() {
    if (!this._cursor) return false;
    this._cursor = null;
    return true;
  }
  browseLength() {
    return this._cursor?.rows.length ?? 0;
  }
  async moveFirst() {
    const cur = this._assertCursor();
    if (cur.rows.length === 0) return false;
    cur.index = 0;
    return true;
  }
  async moveLast() {
    const cur = this._assertCursor();
    if (cur.rows.length === 0) return false;
    cur.index = cur.rows.length - 1;
    return true;
  }
  /**
   * #313 — PARIDAD ANDROID: desbordar TERMINA el recorrido; no deja el cursor
   * apoyado en la ultima fila ni en un EOF del que se pueda "volver".
   *
   *   xonedataobject_lib/.../XoneDataCollection.java:5995 (rama en memoria)
   *     if (++m_nCurrentIndex >= m_nBrowseLength) m_nCurrentIndex = -1;
   *   ...:6046 MovePtr(Next) (rama con recordset)
   *     if (!bHasNext) { m_rs.close(); m_rs = null; ... }   // cierra el recorrido
   *
   * ANTES el indice se incrementaba SIEMPRE y solo despues se avisaba de que
   * se habia pasado, asi que quedaba en `length` y un `movePrevious` posterior
   * devolvia la ULTIMA fila. Ni eso ni lo que pedia el test antiguo (la
   * PENULTIMA) tienen respaldo en Android: alli, tras desbordar, no hay item.
   *
   * `movePrevious` no necesita cambio: su guarda `index <= 0` ya deja el -1
   * intacto, igual que Android (`m_nCurrentIndex -= (m_nCurrentIndex > 0)?1:0`).
   */
  async moveNext() {
    const cur = this._assertCursor();
    if (cur.index < 0) return false;
    if (cur.index + 1 >= cur.rows.length) {
      cur.index = -1;
      return false;
    }
    cur.index += 1;
    return true;
  }
  async movePrevious() {
    const cur = this._assertCursor();
    if (cur.index <= 0) return false;
    cur.index -= 1;
    return true;
  }
  // -------------------------------------------------------------------------
  // Acceso a items
  // -------------------------------------------------------------------------
  get(indexOrKey) {
    if (typeof indexOrKey === "number") {
      return this._items[indexOrKey] ?? null;
    }
    const idField = this.getIdFieldName();
    for (const it of this._items) {
      if (String(it.getValue(idField)) === indexOrKey) return it;
    }
    return null;
  }
  /**
   * Acceso sync por índice / clave: paridad Android `XoneDataCollection#getObject`
   * y los scripts legacy que hacen `var r = rec.getItem(0); r.TITULO;`
   * (sin await). El método solo lee `_items` ya cargado; `loadAll` se
   * encarga de la parte async.
   */
  getObject(...args) {
    if (args.length === 1) {
      const a = args[0];
      if (typeof a === "number") {
        const hit = this._items[a] ?? null;
        if (hit === null && !this._fullyLoaded) {
          this._logger.warn(
            `[xone-data] getObject/getItem(${a}) sobre la colecci\xF3n '${this._name}' a\xFAn no cargada (_fullyLoaded=false, ${this._items.length} items) \u2014 posible carrera de hidrataci\xF3n: el handler ley\xF3 datos antes de que terminase su loadAll. Devuelve null.`
          );
        }
        return hit;
      }
      if (typeof a === "string") return this.get(a);
      if (a instanceof DataObject) return a;
    }
    return null;
  }
  /** Alias sync de {@link getObject}. */
  getItem(...args) {
    return this.getObject(...args);
  }
  async findObject(field, value, ...rest) {
    if (!this._fullyLoaded) {
      await this.loadAll();
    }
    if (value === void 0 && typeof field === "string" && /[=<>]|\sLIKE\s/i.test(field)) {
      const developed = this._developMacros(field);
      try {
        const probe = await this.createClone();
        probe.setFilter(developed);
        await probe.loadAll();
        return probe.get(0);
      } catch {
        for (const it of this._items) {
          const row = it.toJson();
          if (this._applyClientFilter([row], developed).length > 0) return it;
        }
        return null;
      }
    }
    const pairs = [[field, value]];
    for (let i = 0; i + 1 < rest.length; i += 2) {
      pairs.push([rest[i], rest[i + 1]]);
    }
    for (const it of this._items) {
      if (pairs.every(([f, v]) => it.getValue(f) === v)) return it;
    }
    return null;
  }
  async findAllObjects(...args) {
    if (!this._fullyLoaded) await this.loadAll();
    if (args.length === 0) return [...this._items];
    const pairs = [];
    for (let i = 0; i + 1 < args.length; i += 2) {
      pairs.push([args[i], args[i + 1]]);
    }
    return this._items.filter(
      (it) => pairs.every(([f, v]) => it.getValue(f) === v)
    );
  }
  // -------------------------------------------------------------------------
  // Creacion / eliminacion / guardado
  // -------------------------------------------------------------------------
  async createObject(initialValues) {
    const initial = { ...initialValues ?? {} };
    if (!initial["ROWID"]) {
      initial["ROWID"] = generateRowId2();
    }
    const typeSeeded = /* @__PURE__ */ new Set();
    for (const f of this._definition.fields) {
      if (f.name in initial || f.name.startsWith("MAP_")) continue;
      if (f.defaultValue !== void 0) {
        initial[f.name] = f.defaultValue;
        continue;
      }
      const t = String(f.type ?? "").toUpperCase();
      if (t === "N" || /^N[1-6]$/.test(t) || t === "R" || t === "F" || t === "M") {
        initial[f.name] = 0;
        typeSeeded.add(f.name.toUpperCase());
      }
    }
    const obj = this._instantiate(initial, true, typeSeeded);
    this._items.push(obj);
    this._reindex();
    const createEv = this._definition.lifecycleEvents?.find(
      (e) => e.eventName === "create" && e.scriptSource.trim().length > 0
    );
    if (createEv && this._runtime) {
      const scope = {
        kind: "object",
        self: obj,
        This: obj,
        ThisDataColl: this,
        locals: {}
      };
      try {
        const r = await this._runtime.executeScript(createEv.scriptSource, scope);
        if (r === false) {
          this._logger.warn(
            `[create] script de <create> en '${this._name}' retorno false; el DataObject se devuelve igualmente (Android propagaria excepcion).`
          );
        }
      } catch (err) {
        this._logger.error(
          `[create] script de <create> en '${this._name}' lanzo:`,
          err
        );
      }
    }
    Object.defineProperty(obj, "__xoneCreateFired", {
      value: true,
      enumerable: false,
      writable: false,
      configurable: false
    });
    if (this._bus) {
      await this._bus.emitObject("create", { self: obj });
    }
    return obj;
  }
  async addItem(item) {
    if (!(item instanceof DataObject) && !(item && typeof item === "object")) {
      return false;
    }
    try {
      item.markUserAdded?.();
    } catch {
    }
    if (this._items.includes(item)) {
      return true;
    }
    const rowid = item.getValue?.("ROWID");
    if (rowid !== void 0 && rowid !== null && rowid !== "") {
      for (const existing of this._items) {
        if (existing === item) return true;
        if (existing.getValue?.("ROWID") === rowid) return true;
      }
    }
    this._items.push(item);
    this._reindex();
    return true;
  }
  async deleteItem(indexOrItem) {
    const obj = typeof indexOrItem === "number" ? this._items[indexOrItem] ?? null : indexOrItem;
    if (!obj) return false;
    await obj.deleteObject();
    return true;
  }
  async removeItem(indexOrItem) {
    return this.deleteItem(indexOrItem);
  }
  async deleteAll() {
    const copy = [...this._items];
    for (const it of copy) await it.deleteObject();
    return true;
  }
  async browseDeleteAll() {
    return this.deleteAll();
  }
  /** Borrado masivo sin disparar eventos `<before-delete>`/`<after-delete>`. */
  async browseDeleteWithNoRules() {
    const linkedTo = this._definition.linkedTo;
    if (linkedTo && linkedTo.kind === "rest" && this._structuredClient) {
      for (const it of this._items) {
        const [keyField, keyValue] = it._restKey(this._definition);
        if (keyValue) {
          await this._structuredClient.remove(linkedTo.collection, keyValue, keyField);
        }
      }
    } else {
      const def = this._definition;
      const tableName = (def.tablePrefix ?? "") + def.tableName;
      const where = this.getDevelopedFilter();
      const sql = where ? `DELETE FROM ${tableName} WHERE ${where}` : `DELETE FROM ${tableName}`;
      await this._storage.executeSql(sql);
    }
    this._items = [];
    this._reindex();
    return true;
  }
  clear() {
    if (this._locked) {
      return true;
    }
    this._items = [];
    this._currentIndex = -1;
    this._fullyLoaded = false;
    return true;
  }
  async saveAll() {
    for (const it of this._items) {
      await it.save();
    }
    return true;
  }
  async createClone() {
    const clone = new _DataCollection({
      name: this._name,
      definition: this._definition,
      storage: this._storage,
      structuredClient: this._structuredClient,
      macros: this._globalMacros,
      ...this._bus ? { bus: this._bus } : {},
      ...this._definitionResolver ? { definitionResolver: this._definitionResolver } : {},
      ...this._onRowsLoaded ? { onRowsLoaded: this._onRowsLoaded } : {},
      appData: this._appData,
      parent: this._parent
    });
    clone._filter = this._filter;
    clone._linkFilter = this._linkFilter;
    clone._sort = this._sort;
    for (const [k, v] of Object.entries(this._macros.getAllMacros())) {
      clone._macros.setMacro(k, v);
    }
    return clone;
  }
  async swapItems(indexA, indexB) {
    const a = this._items[indexA];
    const b = this._items[indexB];
    if (!a || !b) return false;
    this._items[indexA] = b;
    this._items[indexB] = a;
    this._reindex();
    return true;
  }
  // -------------------------------------------------------------------------
  // Persistencia / locks / JSON / SQL
  // -------------------------------------------------------------------------
  /**
   * Paridad Android (`XoneDataCollection.java:9660` → `Lock():7358`):
   * `lock()` es síncrono y solo activa el flag. Una colección bloqueada
   * ignora `clear()` y `loadAll()` (no-op silencioso, devuelven true).
   */
  lock() {
    this._locked = true;
  }
  /** Paridad Android (`XoneDataCollection.java:9665` → `Unlock():7366`). */
  unlock() {
    this._locked = false;
  }
  async loadFromJson(arrayOrJson) {
    let parsed;
    try {
      parsed = typeof arrayOrJson === "string" ? JSON.parse(arrayOrJson) : arrayOrJson;
    } catch (err) {
      throw new XoneDataError("JSON invalido en loadFromJson.", {
        code: XONE_DATA_ERROR_CODES.JSON_INVALID,
        collection: this._name,
        cause: err
      });
    }
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    this._items = arr.map((row) => this._instantiate({ ...row }, false));
    this._reindex();
    this._fullyLoaded = true;
    this._currentIndex = this._items.length > 0 ? 0 : -1;
    return this;
  }
  async toJson(_options) {
    return this._items.map((it) => it.toJson());
  }
  async executeSqlString(sql, params) {
    const developed = this._developMacros(sql);
    const r = await this._storage.executeSql(developed, params);
    return r;
  }
  async createPersistData(_fieldsOrTable, _fields) {
  }
  async doSort(criteria) {
    const sort = criteria ?? this._sort;
    if (!sort) return;
    const parts = sort.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    this._items.sort((a, b) => {
      for (const part of parts) {
        const [field, dir] = part.split(/\s+/);
        if (!field) continue;
        const va = a.getValue(field);
        const vb = b.getValue(field);
        if (va === vb) continue;
        if (va === null || va === void 0) return 1;
        if (vb === null || vb === void 0) return -1;
        const cmp = va < vb ? -1 : 1;
        return (dir ?? "ASC").toUpperCase() === "DESC" ? -cmp : cmp;
      }
      return 0;
    });
    this._reindex();
  }
  async doSearch(criteria, _tableName) {
    if (!this._fullyLoaded) await this.loadAll();
    const lower = criteria.toLowerCase();
    this._items = this._items.filter((it) => {
      for (const f of this._definition.fields) {
        const v = it.getValue(f.name);
        if (typeof v === "string" && v.toLowerCase().includes(lower)) {
          return true;
        }
      }
      return false;
    });
    this._reindex();
  }
  createSearchIndex(_fieldOrFields, _maybeFields) {
  }
  bind(..._args) {
  }
  unbind(..._args) {
  }
  clearCaches() {
  }
  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  _reindex() {
    for (let i = 0; i < this._items.length; i++) {
      const item = this._items[i];
      if (!item) continue;
      item._index = i;
    }
    if (this._currentIndex >= this._items.length) {
      this._currentIndex = this._items.length - 1;
    }
  }
  _assertCursor() {
    if (!this._cursor || !this._cursor.active) {
      throw new XoneDataError("No hay browse activo. Llama startBrowse primero.", {
        code: XONE_DATA_ERROR_CODES.BROWSE_INVALID_STATE,
        collection: this._name
      });
    }
    return this._cursor;
  }
  _developMacros(text) {
    if (!text) return "";
    let s = text;
    if (this._fieldMacroOwner) {
      s = developFieldValueMacros(s, this._fieldMacroOwner, { prefix: "FLD_", suppressQuotes: true });
      s = developFieldValueMacros(s, this._fieldMacroOwner, { prefix: "OWN_", suppressQuotes: true });
    }
    const globalMacros = this._globalMacros ? this._globalMacros.getAllGlobalMacros() : {};
    return replaceMacrosSql(s, {
      globalMacros,
      collectionMacros: this._macros.getAllMacros()
    });
  }
  _buildSelectSql(opts) {
    const def = this._definition;
    const table = (def.tablePrefix ?? "") + def.tableName;
    const wherePieces = [];
    if (this._filter) wherePieces.push(this._filter);
    if (this._linkFilter) wherePieces.push(this._linkFilter);
    const whereSql = wherePieces.length > 0 ? ` WHERE ${wherePieces.join(" AND ")}` : "";
    const orderSql = this._sort ? ` ORDER BY ${this._sort}` : "";
    const sql = `SELECT * FROM ${table}${whereSql}${orderSql}`;
    return opts.raw ? sql : this._developMacros(sql);
  }
};
function createDataCollection(opts) {
  return new DataCollection(opts);
}

// src/adapter/MemoryStorageAdapter.ts
var MemoryStorageAdapter = class {
  tables = /* @__PURE__ */ new Map();
  /** Crea una tabla con columnas (no usado por SQL, solo para inicializar). */
  seedTable(tableName, rows = []) {
    this.tables.set(tableName, { rows: [...rows] });
  }
  async executeSql(sql, params) {
    const trimmed = sql.trim();
    const upper = trimmed.toUpperCase();
    const paramsArr = this.toArray(params);
    if (upper.startsWith("INSERT INTO")) return this.handleInsert(trimmed, paramsArr);
    if (upper.startsWith("UPDATE ")) return this.handleUpdate(trimmed, paramsArr);
    if (upper.startsWith("DELETE FROM")) return this.handleDelete(trimmed, paramsArr);
    if (upper.startsWith("SELECT")) return this.handleSelect(trimmed, paramsArr);
    return { rows: [], affectedRows: 0 };
  }
  async transaction(fn) {
    const snapshot = /* @__PURE__ */ new Map();
    for (const [k, v] of this.tables) snapshot.set(k, [...v.rows]);
    try {
      return await fn(this);
    } catch (err) {
      for (const [k, v] of snapshot) {
        this.tables.set(k, { rows: v });
      }
      throw err;
    }
  }
  isReady() {
    return true;
  }
  // -------------------------------------------------------------------------
  // SQL minimo
  // -------------------------------------------------------------------------
  handleInsert(sql, params) {
    const m = sql.match(
      /^INSERT\s+INTO\s+(\S+)\s*\(([^)]+)\)\s*VALUES\s*\(([^)]+)\)\s*;?\s*$/i
    );
    if (!m) return { rows: [], affectedRows: 0 };
    const tableName = m[1];
    const colsRaw = m[2];
    const cols = colsRaw.split(",").map((s) => s.trim());
    const table = this.ensureTable(tableName);
    const row = {};
    for (let i = 0; i < cols.length; i++) {
      const colName = cols[i];
      if (!colName) continue;
      row[colName.toUpperCase()] = params[i] ?? null;
    }
    table.rows.push(row);
    return { rows: [], affectedRows: 1 };
  }
  handleUpdate(sql, params) {
    const m = sql.match(
      /^UPDATE\s+(\S+)\s+SET\s+(.+?)\s+WHERE\s+(\S+)\s*=\s*\?\s*;?\s*$/i
    );
    if (!m) return { rows: [], affectedRows: 0 };
    const tableName = m[1];
    const setClauseRaw = m[2];
    const whereField = m[3];
    const setClause = setClauseRaw.split(",").map((s) => s.trim());
    const table = this.tables.get(tableName);
    if (!table) return { rows: [], affectedRows: 0 };
    const whereValue = params[params.length - 1];
    let affected = 0;
    const matchingRows = table.rows.filter(
      (r) => String(this.caseGet(r, whereField)) === String(whereValue)
    );
    for (const r of matchingRows) {
      for (let i = 0; i < setClause.length; i++) {
        const part = setClause[i];
        if (!part) continue;
        const fieldMatch = part.match(/^(\S+)\s*=\s*\?$/);
        if (!fieldMatch) continue;
        const fieldName = fieldMatch[1];
        const realKey = this.caseKey(r, fieldName);
        r[realKey] = params[i] ?? null;
      }
      affected += 1;
    }
    return { rows: [], affectedRows: affected };
  }
  handleDelete(sql, params) {
    const m = sql.match(
      /^DELETE\s+FROM\s+(\S+)(?:\s+WHERE\s+(\S+)\s*=\s*\?)?\s*;?\s*$/i
    );
    if (!m) return { rows: [], affectedRows: 0 };
    const tableName = m[1];
    const whereField = m[2];
    const table = this.tables.get(tableName);
    if (!table) return { rows: [], affectedRows: 0 };
    if (!whereField) {
      const n = table.rows.length;
      table.rows = [];
      return { rows: [], affectedRows: n };
    }
    const whereValue = params[0];
    const before = table.rows.length;
    table.rows = table.rows.filter(
      (r) => String(this.caseGet(r, whereField)) !== String(whereValue)
    );
    return { rows: [], affectedRows: before - table.rows.length };
  }
  handleSelect(sql, _params) {
    const m = sql.match(
      /^SELECT\s+(?:\*|[^\s]+(?:\s*,\s*[^\s]+)*)\s+FROM\s+(\S+)(?:\s+WHERE\s+(.+?))?(?:\s+ORDER\s+BY\s+(.+?))?(?:\s+LIMIT\s+\d+)?\s*;?\s*$/i
    );
    if (!m) return { rows: [], affectedRows: 0 };
    const tableName = m[1];
    const where = m[2];
    const orderBy = m[3];
    const table = this.tables.get(tableName);
    if (!table) return { rows: [], affectedRows: 0 };
    let rows = [...table.rows];
    if (where) rows = rows.filter((r) => this.evalSimpleWhere(where, r));
    if (orderBy) rows = this.applyOrderBy(rows, orderBy);
    return { rows, affectedRows: rows.length };
  }
  evalSimpleWhere(where, row) {
    const conds = where.split(/\s+AND\s+/i);
    for (const c of conds) {
      const m = c.match(
        /^\s*([A-Za-z0-9_]+)\s*(=|!=|<>|<=|>=|<|>|LIKE)\s*(.+?)\s*$/i
      );
      if (!m) continue;
      const field = m[1];
      const op = m[2];
      let literal = m[3].trim();
      if (typeof literal === "string" && literal.startsWith("'") && literal.endsWith("'")) {
        literal = literal.slice(1, -1);
      } else if (typeof literal === "string" && /^-?\d+(\.\d+)?$/.test(literal)) {
        literal = Number(literal);
      }
      const v = this.caseGet(row, field);
      const opOk = (() => {
        switch (op.toUpperCase()) {
          case "=":
            return String(v) === String(literal);
          case "!=":
          case "<>":
            return String(v) !== String(literal);
          case "<":
            return v < literal;
          case "<=":
            return v <= literal;
          case ">":
            return v > literal;
          case ">=":
            return v >= literal;
          case "LIKE":
            return String(v).includes(String(literal).replace(/%/g, ""));
        }
        return false;
      })();
      if (!opOk) return false;
    }
    return true;
  }
  applyOrderBy(rows, orderBy) {
    const parts = orderBy.split(",").map((s) => s.trim()).filter(Boolean);
    return [...rows].sort((a, b) => {
      for (const p of parts) {
        const [field, dir] = p.split(/\s+/);
        if (!field) continue;
        const va = this.caseGet(a, field);
        const vb = this.caseGet(b, field);
        if (va === vb) continue;
        if (va == null) return 1;
        if (vb == null) return -1;
        const cmp = va < vb ? -1 : 1;
        return (dir ?? "ASC").toUpperCase() === "DESC" ? -cmp : cmp;
      }
      return 0;
    });
  }
  ensureTable(name) {
    let t = this.tables.get(name);
    if (!t) {
      t = { rows: [] };
      this.tables.set(name, t);
    }
    return t;
  }
  toArray(params) {
    if (!params) return [];
    if (Array.isArray(params)) return [...params];
    return Object.values(params);
  }
  /** Case-insensitive key lookup (SQL columns are case-insensitive). */
  caseGet(row, field) {
    if (field in row) return row[field];
    const upper = field.toUpperCase();
    if (upper in row) return row[upper];
    const lower = field.toLowerCase();
    if (lower in row) return row[lower];
    return void 0;
  }
  /** Returns the actual key name in the row matching `field` case-insensitively. */
  caseKey(row, field) {
    if (field in row) return field;
    const upper = field.toUpperCase();
    if (upper in row) return upper;
    const lower = field.toLowerCase();
    if (lower in row) return lower;
    return field;
  }
  /** Acceso directo para tests (no es parte del contrato IStorageAdapter). */
  _getRowsForTest(tableName) {
    return this.tables.get(tableName)?.rows ?? [];
  }
};

// src/adapters/XoneRemoteStorageAdapter.ts
var READ_RE = /^\s*(SELECT|WITH|PRAGMA|EXPLAIN)\b/i;
var XoneRemoteStorageAdapter = class {
  backend;
  macros;
  apiBase;
  logger;
  constructor(opts) {
    this.backend = opts.backend;
    this.macros = opts.macros;
    this.apiBase = (opts.apiBase ?? "").replace(/\/+$/, "");
    this.logger = opts.logger ?? console;
  }
  isReady() {
    return true;
  }
  /**
   * Plan 14 Fase 4 — `executeSql` está retirado. Cualquier intento lanza
   * inmediatamente con el código <c>XONE.RAW.REMOVED</c>. El adapter sólo
   * sobrevive como shim de compilación; en runtime nunca debe ejecutar SQL.
   *
   * Migración:
   *   - Listas/CRUD simple → `linkedTo` en el `.xne` → REST + DSL via
   *     `XoneStructuredDataClient.list/get/create/update/remove`.
   *   - Queries complejas (joins, subselects, agregados) → catálogo nombrado
   *     pre-compilado (Fase 3 emite las entradas automáticamente).
   */
  async executeSql(_sql, _params) {
    void _sql;
    void _params;
    throw new Error(
      "XONE.RAW.REMOVED: XoneRemoteStorageAdapter.executeSql est\xE1 retirado en Plan 14 Fase 4. El SPA no debe ejecutar SQL crudo en cliente. Migra la colecci\xF3n a `linkedTo` (REST + DSL o cat\xE1logo nombrado). Docs: https://docs.xone.es/migration/raw-removed"
    );
  }
  // Sin TX remota; backend ejecuta autocommit por sentencia.
  async transaction(fn) {
    return fn(this);
  }
  /** No-op: no hay conexion persistente que cerrar. */
  close() {
  }
  // ---------------------------------------------------------------------------
  // Internos
  // ---------------------------------------------------------------------------
  async runRemoteRead(sql, params) {
    const dq = this.backend.dataQuery;
    if (dq && typeof dq.executeRaw === "function") {
      try {
        const res = await dq.executeRaw({ SQL: sql, PARAMS: params });
        return res?.FILAS ?? res?.REGISTROS ?? [];
      } catch (err) {
        this.logger.warn("[XoneRemoteStorageAdapter] executeRaw fallo:", err);
        return [];
      }
    }
    return this.fetchRaw(sql, params);
  }
  async fetchRaw(sql, params) {
    const url = `${this.apiBase}/api/v1/query/raw`;
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json"
    };
    const token = this.macros?.getGlobalMacro("TOKEN");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const f = globalThis.fetch;
    if (!f) {
      this.logger.warn(
        "[XoneRemoteStorageAdapter] No hay fetch disponible; devolviendo [] para:",
        sql
      );
      return [];
    }
    try {
      const res = await f(url, {
        method: "POST",
        headers,
        body: JSON.stringify({ SQL: sql, PARAMS: params })
      });
      if (!res.ok) {
        this.logger.warn(
          `[XoneRemoteStorageAdapter] POST ${url} -> HTTP ${res.status}. Asegurate de que XoneBackendWeb expone el endpoint /api/v1/query/raw.`
        );
        return [];
      }
      const json = await res.json();
      return json?.FILAS ?? json?.REGISTROS ?? [];
    } catch (err) {
      this.logger.warn("[XoneRemoteStorageAdapter] fetch raw fallo:", err);
      return [];
    }
  }
  /**
   * Red de seguridad: expande macros globales `##NAME##` que pudieran
   * llegar sin desarrollar al adapter. Ignora los prefijos que son
   * responsabilidad de `xone-data` (ver `DataCollection._developMacros`
   * y `developFieldValueMacros`) — FLD_, OWN_, ID, CLIENTIDCOLL, MULTIKEY,
   * ISNULL — porque esos requieren un DataObject contextual que el
   * adapter no conoce. Si llegan hasta aquí sin resolver, el caller no
   * inyectó el owner (`DataCollection.setFieldMacroOwner`) y devolver
   * NULL silenciosamente sería incorrecto: dejamos la macro tal cual
   * para que Postgres devuelva un error explícito.
   */
  expandMacros(sql) {
    if (!this.macros || !sql.includes("##")) return sql;
    return sql.replace(/##([A-Z0-9_]+)##/g, (match, name) => {
      if (name.startsWith("FLD_") || name.startsWith("OWN_") || name === "ID" || name === "CLIENTIDCOLL" || name === "MULTIKEY" || name.startsWith("ISNULL")) {
        return match;
      }
      const v = this.macros?.getGlobalMacro(name);
      if (v === null || v === void 0 || v === "") return "NULL";
      const s = String(v);
      if (/^-?\d+(\.\d+)?$/.test(s)) return s;
      return "'" + s.replace(/'/g, "''") + "'";
    });
  }
  toArray(params) {
    if (!params) return [];
    if (Array.isArray(params)) return params;
    return Object.values(params);
  }
  extractTableHint(sql) {
    const m = /(?:INTO|UPDATE|FROM)\s+([A-Za-z_][A-Za-z0-9_]*)/i.exec(sql);
    return m ? m[1] ?? null : null;
  }
};
function createRemoteStorage(opts) {
  const adapter = new XoneRemoteStorageAdapter(opts);
  const dbExecuteSql = async (sql, params) => {
    const r = await adapter.executeSql(sql, params);
    return READ_RE.test(sql) ? r.rows : r.affectedRows;
  };
  return {
    adapter,
    db: {
      executeSql: dbExecuteSql,
      async transaction(fn) {
        return fn({
          executeSql: async (s, p) => dbExecuteSql(s, p)
        });
      },
      close() {
        adapter.close();
      },
      isOpen() {
        return adapter.isReady();
      }
    }
  };
}

// src/clients/XoneStructuredDataClient.ts
function parseLinkedTo(raw) {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed.startsWith("rest:")) {
    const collection = trimmed.slice(5).trim();
    if (!collection) return null;
    return { kind: "rest", collection };
  }
  if (trimmed.startsWith("cat:")) {
    const name = trimmed.slice(4).trim();
    if (!name) return null;
    return { kind: "cat", name };
  }
  if (trimmed.startsWith("external:")) {
    const provider = trimmed.slice(9).trim();
    if (!provider) return null;
    return { kind: "external", provider };
  }
  return null;
}
var XoneStructuredDataClient = class {
  constructor(backend) {
    this.backend = backend;
  }
  backend;
  /** ¿El backend acepta `where` SQL crudo (WebCore)? Ver `dsl.rawWhere`. */
  supportsRawWhere() {
    return this.backend.supportsRawWhere === true;
  }
  /** Dialecto de filtros declarado por el backend (`simple` si no declara nada). */
  filterDialect() {
    return resolveFilterDialect(this.backend.filterDialect);
  }
  /** REST list. */
  async list(collection, dsl) {
    const resp = await this.backend.collection.list(collection, dsl);
    return resp.ROWS;
  }
  /** REST list devolviendo la respuesta completa (page metadata + cursor). */
  async listPage(collection, dsl) {
    return this.backend.collection.list(collection, dsl);
  }
  async get(collection, rowid, keyField) {
    const resp = await this.backend.collection.get(collection, rowid, keyField);
    return resp.ROW;
  }
  async count(collection, filter) {
    const resp = await this.backend.collection.count(collection, filter);
    return resp.TOTAL;
  }
  async create(collection, row) {
    const resp = await this.backend.collection.create(collection, row);
    return { ROWID: resp.ROWID, newId: resp.newId ?? null };
  }
  async update(collection, rowid, row, keyField) {
    await this.backend.collection.update(collection, rowid, row, keyField);
  }
  async remove(collection, rowid, keyField) {
    await this.backend.collection.remove(collection, rowid, keyField);
  }
  /**
   * Catálogo nombrado. El nombre se prefija con `projectId` en build-time
   * (el cliente recibe ya el nombre completo `<projectId>.<queryName>`).
   */
  async runNamed(name, parametros = {}, opts) {
    const orden = opts?.order?.map((o) => ({
      CAMPO: o.field,
      DIRECCION: (o.dir ?? "asc").toUpperCase()
    }));
    const paginacion = opts?.offset !== void 0 || opts?.limit !== void 0 ? { OFFSET: opts?.offset ?? 0, LIMIT: opts?.limit ?? 100 } : void 0;
    const resp = await this.backend.dataQuery.execute(name, {
      PARAMETROS: parametros,
      PAGINACION: paginacion,
      ORDEN: orden
    });
    return resp.REGISTROS;
  }
};
export {
  ALWAYS_FALSE,
  ALWAYS_TRUE,
  DEFAULT_LIMITS,
  DataCollection,
  DataObject,
  DirtyTracker,
  DslBuilder,
  FULL_DIALECT,
  MemoryStorageAdapter,
  SIMPLE_DIALECT,
  XONE_DATA_ERROR_CODES,
  XoneDataError,
  XoneRemoteStorageAdapter,
  XoneStructuredDataClient,
  assertUpperCaseField,
  broadcastRowWrite,
  coerceToFieldType,
  createDataCollection,
  createDataObject,
  createRemoteStorage,
  developFieldValueMacros,
  developObjectValue,
  isPrivateOrReservedProp,
  onRowWrite,
  parseLinkedTo,
  parseWhereToDsl,
  pendingLoadCount,
  pendingWritesCount,
  resolveFilterDialect,
  trackPendingLoad,
  trackPendingWrite,
  translateLikePattern,
  whenPendingLoadsSettled,
  whenWritesIdle,
  withinServerLimits,
  wrapWithProxy
};
//# sourceMappingURL=index.js.map